import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import GenesisGenerarPreguntas from './GenesisGenerarPreguntas';
import EscalaRating from './EscalaRating';

/**
 * Devuelve cómo embeber un documento (PDF / Office / Google Drive) según su URL.
 *
 * Estrategia:
 *  - Si es link de Google Drive (drive.google.com/file/d/ID/...) → usa el
 *    visor nativo de Drive (`/preview`). Soporta PDF, PPT, DOCX, XLSX, video,
 *    imágenes, etc. NO requiere que el archivo sea público en internet más
 *    allá del propio share de Drive.
 *  - Si la URL termina en .ppt/.pptx/.pps/.ppsx/.doc/.docx/.xls/.xlsx → usa
 *    Office Online Viewer (Microsoft renderiza el archivo en su servidor).
 *    Requiere que la URL sea descargable públicamente desde internet.
 *  - .pdf → embed directo en iframe.
 *  - Cualquier otro → solo botón "Abrir" / "Descargar".
 *
 * @param {string} url
 * @returns {{ type: 'drive'|'office'|'pdf'|'external', embedUrl: string|null, downloadUrl: string, openUrl: string } | null}
 */
function getDocumentoEmbedConfig(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();

  // 1) Google Drive: extraer file ID
  // Formatos soportados:
  //   https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  //   https://drive.google.com/file/d/FILE_ID/edit
  //   https://drive.google.com/open?id=FILE_ID
  //   https://drive.google.com/uc?export=download&id=FILE_ID
  const driveMatch = u.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:[^#]*&)?id=)([\w-]{15,})/);
  if (driveMatch) {
    const fileId = driveMatch[1];
    return {
      type: 'drive',
      embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
      downloadUrl: `https://drive.google.com/uc?export=download&id=${fileId}`,
      openUrl: u,
    };
  }

  // 2) Office docs por extensión
  const clean = u.split('?')[0].split('#')[0];
  const ext = (clean.split('.').pop() || '').toLowerCase();
  const OFFICE_EXTS = ['ppt', 'pptx', 'pps', 'ppsx', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'odp', 'ods'];
  if (OFFICE_EXTS.includes(ext)) {
    return {
      type: 'office',
      embedUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(u)}`,
      downloadUrl: u,
      openUrl: u,
    };
  }

  // 3) PDF directo
  if (ext === 'pdf') {
    return {
      type: 'pdf',
      embedUrl: u,
      downloadUrl: u,
      openUrl: u,
    };
  }

  // 4) Otro tipo: solo botones, sin embed
  return {
    type: 'external',
    embedUrl: null,
    downloadUrl: u,
    openUrl: u,
  };
}

// ACTUALIZADO: Diseño vertical móvil + Banner de clase - v2.0
// Props:
// - claseIdProp: si se pasa, fuerza el filtro de clase (ignora URL search params)
// - embedded: si true, oculta el header grande (para empotrar dentro de otra vista)
const PruebasExamenes = ({ claseIdProp = null, embedded = false } = {}) => {
  const { user, API } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const claseIdFiltro = claseIdProp || searchParams.get('clase_id'); // Filtro de clase desde prop o URL
  
  const [pruebas, setPruebas] = useState([]);
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrueba, setSelectedPrueba] = useState(null);
  const [claseSeleccionada, setClaseSeleccionada] = useState(claseIdFiltro || null);
  const [filtroEstadoClase, setFiltroEstadoClase] = useState('activas'); // Por defecto mostrar solo activas
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [misRespuestasDetalle, setMisRespuestasDetalle] = useState(null);
  const [loadingMisRespuestas, setLoadingMisRespuestas] = useState(false);
  const [isPreguntasDialogOpen, setIsPreguntasDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPrueba, setEditingPrueba] = useState(null);
  const [isAddPreguntaDialogOpen, setIsAddPreguntaDialogOpen] = useState(false);
  const [isTomandoPruebaDialogOpen, setIsTomandoPruebaDialogOpen] = useState(false);
  const [isRespuestasDialogOpen, setIsRespuestasDialogOpen] = useState(false);
  const [isCalificarDialogOpen, setIsCalificarDialogOpen] = useState(false);
  const [isVerResultadosEstudianteDialogOpen, setIsVerResultadosEstudianteDialogOpen] = useState(false);
  const [respuestasRecibidas, setRespuestasRecibidas] = useState([]);
  const [respuestaSeleccionada, setRespuestaSeleccionada] = useState(null);
  const [calificaciones, setCalificaciones] = useState({});
  const [miRespuestaCalificada, setMiRespuestaCalificada] = useState(null);
  const [tipoPreguntaNueva, setTipoPreguntaNueva] = useState('desarrollo');
  const [editingPreguntaId, setEditingPreguntaId] = useState(null);
  const [respuestaActual, setRespuestaActual] = useState(null);
  const [respuestasEstudiante, setRespuestasEstudiante] = useState([]);
  const [estudiantesNoRespondieron, setEstudiantesNoRespondieron] = useState([]);
  const [infoNoRespondieron, setInfoNoRespondieron] = useState(null);
  const [calificandoNoRespondieron, setCalificandoNoRespondieron] = useState(false);
  const [reenviarLoading, setReenviarLoading] = useState(false);
  const [isReabrirDialogOpen, setIsReabrirDialogOpen] = useState(false);
  const [reabrirPrueba, setReabrirPrueba] = useState(null);
  const [reabrirEstudiantes, setReabrirEstudiantes] = useState([]);
  const [reabrirSeleccionados, setReabrirSeleccionados] = useState([]);
  const [reabrirFecha, setReabrirFecha] = useState('');
  const [reabrirLoading, setReabrirLoading] = useState(false);
  const [isGenesisGenOpen, setIsGenesisGenOpen] = useState(false);
  // Review (escala_1_10 marcada como es_review): histograma agregado para profesor/admin
  const [isReviewResultadosOpen, setIsReviewResultadosOpen] = useState(false);
  const [reviewResultados, setReviewResultados] = useState(null);
  const [loadingReviewResultados, setLoadingReviewResultados] = useState(false);
  const [nuevaPregunta, setNuevaPregunta] = useState({
    enunciado: '',
    tipo_respuesta: 'texto_largo',
    puntos: 10,
    criterios: '',
    video_youtube_url: '',
    imagen_url: '',
    pdf_url: '',
    opciones: [
      { letra: 'A', texto: '' },
      { letra: 'B', texto: '' },
      { letra: 'C', texto: '' },
      { letra: 'D', texto: '' }
    ],
    respuesta_correcta: '',
    feedback_correcto: '',
    feedback_incorrecto: '',
    // Para escala_1_10
    etiqueta_izquierda: '',
    etiqueta_derecha: '',
    es_review: false
  });

  // Tipos de ítem que NO son preguntas calificables (sin puntos, sin respuesta del alumno)
  // NOTA: `escala_1_10` ES respondida por el alumno (feedback estilo NPS) pero
  // NO se califica — el profesor la ve pero no le pone puntos. Tratamos a este
  // tipo como "no calificable" para que NO bloquee la autocalificación.
  const TIPOS_NO_CALIFICABLES = ['texto_informativo', 'material_apoyo', 'escala_1_10'];
  const esNoCalificable = (tipo) => TIPOS_NO_CALIFICABLES.includes(tipo);

  // Material de Apoyo SIEMPRE va arriba (el alumno debe revisarlo antes de responder).
  // Conserva el orden relativo del resto.
  const ordenarPreguntasParaMostrar = (preguntas) => {
    if (!Array.isArray(preguntas)) return [];
    const materiales = preguntas.filter(p => p.tipo === 'material_apoyo');
    const otros = preguntas.filter(p => p.tipo !== 'material_apoyo');
    return [...materiales, ...otros];
  };

  const [newPrueba, setNewPrueba] = useState({
    clase_id: '',
    titulo: '',
    descripcion: '',
    tipo: 'quiz',
    modalidad: 'preguntas_sistema',
    link_externo: '',
    fecha_disponible: '',
    fecha_limite: '',
    zona_horaria: 'America/Montevideo', // Default Uruguay
    duracion_minutos: 0,
    puntos_totales: 100,
    peso_nota_final: 20,
    temario: '',
    calificar_automatico_cero: true, // Por defecto marcado
    es_review_prueba: false, // Si true, no genera calificación ni entra en promedio
    es_prueba_final_capacitacion: false, // Disparador de auto-desbloqueo (solo clases autocap)
    config: {
      mostrar_resultados_inmediato: true,
      permitir_archivos: true,
      prevenir_copy_paste: true,
      barajar_preguntas: false
    },
    preguntas: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // AUTO-SAVE: Save student answers to localStorage whenever they change
  useEffect(() => {
    if (respuestasEstudiante.length > 0 && selectedPrueba) {
      const hasContent = respuestasEstudiante.some(r => r.respuesta_texto || r.respuesta_seleccionada);
      if (hasContent) {
        const savedKey = `prueba_respuestas_${selectedPrueba.id}`;
        localStorage.setItem(savedKey, JSON.stringify(respuestasEstudiante));
      }
    }
  }, [respuestasEstudiante, selectedPrueba]);

  const loadData = async () => {
    try {
      const [pruebasRes, clasesRes] = await Promise.all([
        axios.get(`${API}/pruebas-examenes`),
        axios.get(`${API}/clases`)
      ]);
      setPruebas(pruebasRes.data);
      setClases(clasesRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Deep-link: si la URL incluye ?prueba_id=xxx (notificación push de calificación)
  // y la prueba está calificada, abre directamente el modal "Ver Resultados".
  // Caso no calificada: solo hace scroll a la card (no auto-comenzar la prueba).
  const [autoOpenedPruebaId, setAutoOpenedPruebaId] = useState(null);
  useEffect(() => {
    if (loading) return;
    const target = searchParams.get('prueba_id');
    if (!target || autoOpenedPruebaId === target) return;
    const found = pruebas.find((p) => p.id === target);
    if (!found) return;
    setAutoOpenedPruebaId(target);
    setTimeout(async () => {
      if (found.mi_estado_respuesta === 'calificada') {
        try {
          const r = await axios.get(`${API}/pruebas-examenes/mis-respuestas/${found.id}`);
          setSelectedPrueba(found);
          setMiRespuestaCalificada(r.data);
          setIsVerResultadosEstudianteDialogOpen(true);
        } catch (e) {
          console.error('Auto-open prueba calificada:', e);
        }
      } else {
        // Scroll a la card si no está calificada (el usuario decide qué hacer)
        const el = document.querySelector(`[data-prueba-card="${target}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
  }, [loading, pruebas, searchParams, autoOpenedPruebaId, API]);

  const getBannerStyle = (bannerUrl) => {
    if (bannerUrl) {
      // Si la URL ya es completa (empieza con http), usarla directamente
      const imageUrl = bannerUrl.startsWith('http') ? bannerUrl : `${API}${bannerUrl}`;
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    return {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    };
  };

  // Devuelve la fecha límite EFECTIVA según el rol:
  // - Estudiante: su extensión individual si existe, sino la fecha global.
  // - Profesor/Admin: la fecha MÁS LEJANA entre la global y todas las extensiones,
  //   para que la prueba "vuelva a Pendientes" cuando hay reaperturas activas.
  const getLimiteEfectivo = (prueba) => {
    const exts = prueba?.extensiones || [];

    // Estudiante: solo su extensión propia
    if (user?.rol === 'estudiante') {
      const mia = exts.find(e => e.estudiante_id === user.id);
      if (mia && mia.fecha_limite) {
        const d = new Date(mia.fecha_limite);
        if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
          d.setHours(23, 59, 59, 999);
        }
        return d;
      }
      return new Date(prueba.fecha_limite);
    }

    // Profesor/Admin: máximo entre fecha global y todas las extensiones
    const candidatos = [new Date(prueba.fecha_limite)];
    for (const e of exts) {
      if (e?.fecha_limite) {
        const d = new Date(e.fecha_limite);
        if (!isNaN(d.getTime())) {
          if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
            d.setHours(23, 59, 59, 999);
          }
          candidatos.push(d);
        }
      }
    }
    return new Date(Math.max(...candidatos.map(d => d.getTime())));
  };

  const getEstadoBadge = (prueba) => {
    const ahora = new Date();
    const disponible = new Date(prueba.fecha_disponible);
    const limite = getLimiteEfectivo(prueba);

    // Si el estudiante tiene estado de respuesta, mostrarlo primero
    if (prueba.mi_estado_respuesta === 'calificada' && prueba.mi_calificacion !== null) {
      return (
        <Badge className="bg-green-600 text-white font-bold">
          ✅ Calificada: {prueba.mi_calificacion}/{prueba.puntos_totales} pts
        </Badge>
      );
    } else if (prueba.mi_estado_respuesta === 'calificando') {
      return <Badge className="bg-yellow-500">⏳ Enviada - Pendiente de Calificar</Badge>;
    } else if (prueba.mi_estado_respuesta === 'en_progreso') {
      return <Badge className="bg-blue-500 animate-pulse">🔄 En Progreso</Badge>;
    }

    // Si no hay estado de respuesta, mostrar estado según fechas
    if (ahora < disponible) {
      return <Badge className="bg-gray-500">📅 Próximamente</Badge>;
    } else if (ahora > limite) {
      return <Badge className="bg-red-500">⏰ Vencida</Badge>;
    } else {
      const diasRestantes = Math.ceil((limite - ahora) / (1000 * 60 * 60 * 24));
      if (diasRestantes <= 1) {
        return <Badge className="bg-red-500 animate-pulse">⚠️ Vence hoy</Badge>;
      } else if (diasRestantes <= 3) {
        return <Badge className="bg-yellow-500">⏰ {diasRestantes} días</Badge>;
      } else {
        return <Badge className="bg-green-500">✅ Activa</Badge>;
      }
    }
  };

  // Helper para obtener el texto e ícono del botón según el estado de la respuesta
  const getBotonEstudianteInfo = (prueba) => {
    const ahora = new Date();
    const disponible = new Date(prueba.fecha_disponible);
    const limite = getLimiteEfectivo(prueba);

    if (prueba.mi_estado_respuesta === 'calificada') {
      return { texto: 'Ver Mis Resultados', icono: 'fa-eye', clase: 'bg-green-600 hover:bg-green-700', disabled: false };
    } else if (prueba.mi_estado_respuesta === 'calificando') {
      return { texto: 'Ver Respuestas Enviadas', icono: 'fa-clock', clase: 'bg-yellow-600 hover:bg-yellow-700', disabled: false };
    } else if (prueba.mi_estado_respuesta === 'en_progreso') {
      return { texto: 'Continuar Prueba', icono: 'fa-play', clase: 'bg-blue-600 hover:bg-blue-700', disabled: false };
    } else if (ahora < disponible) {
      const diasFaltan = Math.ceil((disponible - ahora) / (1000 * 60 * 60 * 24));
      return { texto: `Disponible en ${diasFaltan} día${diasFaltan > 1 ? 's' : ''}`, icono: 'fa-lock', clase: 'bg-gray-400 cursor-not-allowed', disabled: true };
    } else if (ahora > limite) {
      return { texto: 'Plazo Vencido', icono: 'fa-lock', clase: 'bg-red-400 cursor-not-allowed', disabled: true };
    } else {
      return { texto: 'Responder Prueba', icono: 'fa-pencil-alt', clase: 'bg-green-600 hover:bg-green-700', disabled: false };
    }
  };

  const handleEditarPrueba = (prueba) => {
    setEditingPrueba(prueba);
    setNewPrueba({
      clase_id: prueba.clase_id,
      titulo: prueba.titulo,
      descripcion: prueba.descripcion || '',
      tipo: prueba.tipo,
      modalidad: prueba.modalidad,
      link_externo: prueba.link_externo || '',
      fecha_disponible: (() => { const d = new Date(prueba.fecha_disponible); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      fecha_limite: (() => { const d = new Date(prueba.fecha_limite); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      zona_horaria: prueba.zona_horaria || 'America/Montevideo',
      duracion_minutos: prueba.duracion_minutos || 0,
      puntos_totales: prueba.puntos_totales,
      peso_nota_final: prueba.peso_nota_final,
      temario: prueba.temario || '',
      // Flags / checkboxes (sin esto se cargaban con los defaults y al guardar
      // borraban el estado real de la prueba)
      calificar_automatico_cero: prueba.calificar_automatico_cero !== false, // default true
      es_review_prueba: prueba.es_review_prueba === true,
      es_prueba_final_capacitacion: prueba.es_prueba_final_capacitacion === true,
      requiere_100_puntos: prueba.requiere_100_puntos === true,
      config: prueba.config,
      preguntas: prueba.preguntas || []
    });
    setIsEditDialogOpen(true);
  };

  const handleEliminarPrueba = async (pruebaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta prueba? Solo se puede eliminar si no tiene respuestas.')) {
      return;
    }

    try {
      await axios.delete(`${API}/pruebas-examenes/${pruebaId}`);
      setPruebas(pruebas.filter(p => p.id !== pruebaId));
      toast.success('✅ Prueba eliminada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar prueba');
    }
  };

  const handleActualizarPrueba = async (e) => {
    e.preventDefault();
    
    if (!newPrueba.clase_id || !newPrueba.titulo || !newPrueba.fecha_disponible || !newPrueba.fecha_limite) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      // Convertir fechas: fecha_disponible a 00:00, fecha_limite a 23:59
      const fechaDisponible = new Date(newPrueba.fecha_disponible + 'T00:00:00');
      const fechaLimite = new Date(newPrueba.fecha_limite + 'T23:59:59');
      
      const dataToSend = {
        ...newPrueba,
        fecha_disponible: fechaDisponible.toISOString(),
        fecha_limite: fechaLimite.toISOString()
      };

      const response = await axios.put(`${API}/pruebas-examenes/${editingPrueba.id}`, dataToSend);
      setPruebas(pruebas.map(p => p.id === editingPrueba.id ? response.data : p));
      setIsEditDialogOpen(false);
      setEditingPrueba(null);
      
      toast.success('✅ Prueba actualizada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar prueba');
    }
  };

  const handleAgregarPregunta = async () => {
    // Para texto informativo no requerir puntos
    if (!nuevaPregunta.enunciado) {
      toast.error('Por favor completa el texto');
      return;
    }
    
    if (!esNoCalificable(tipoPreguntaNueva) && nuevaPregunta.puntos <= 0) {
      toast.error('Por favor asigna puntos a la pregunta');
      return;
    }

    const preguntaData = {
      id: editingPreguntaId || `pregunta-${Date.now()}`,
      orden: editingPreguntaId 
        ? (selectedPrueba.preguntas?.find(p => p.id === editingPreguntaId)?.orden || 1)
        : (selectedPrueba.preguntas?.length || 0) + 1,
      tipo: tipoPreguntaNueva,
      enunciado: nuevaPregunta.enunciado,
      puntos: esNoCalificable(tipoPreguntaNueva) ? 0 : nuevaPregunta.puntos,
      tipo_respuesta: !esNoCalificable(tipoPreguntaNueva) ? nuevaPregunta.tipo_respuesta : null,
      criterios: nuevaPregunta.criterios,
      video_youtube_url: nuevaPregunta.video_youtube_url || null,
      imagen_url: nuevaPregunta.imagen_url || null,
      pdf_url: nuevaPregunta.pdf_url || null,
      opciones: tipoPreguntaNueva === 'multiple' ? nuevaPregunta.opciones : null,
      respuesta_correcta: tipoPreguntaNueva !== 'desarrollo' && !esNoCalificable(tipoPreguntaNueva) ? nuevaPregunta.respuesta_correcta : null,
      feedback_correcto: nuevaPregunta.feedback_correcto,
      feedback_incorrecto: nuevaPregunta.feedback_incorrecto,
      // Para escala_1_10 (etiquetas opcionales de los extremos)
      etiqueta_izquierda: tipoPreguntaNueva === 'escala_1_10' ? (nuevaPregunta.etiqueta_izquierda || null) : null,
      etiqueta_derecha: tipoPreguntaNueva === 'escala_1_10' ? (nuevaPregunta.etiqueta_derecha || null) : null,
      es_review: tipoPreguntaNueva === 'escala_1_10' ? !!nuevaPregunta.es_review : false
    };

    try {
      let nuevasPreguntas;
      if (editingPreguntaId) {
        nuevasPreguntas = selectedPrueba.preguntas.map(p => p.id === editingPreguntaId ? preguntaData : p);
      } else {
        nuevasPreguntas = [...(selectedPrueba.preguntas || []), preguntaData];
      }

      const pruebaActualizada = {
        ...selectedPrueba,
        preguntas: nuevasPreguntas,
        fecha_disponible: new Date(selectedPrueba.fecha_disponible).toISOString(),
        fecha_limite: new Date(selectedPrueba.fecha_limite).toISOString()
      };

      const response = await axios.put(`${API}/pruebas-examenes/${selectedPrueba.id}`, pruebaActualizada);
      
      // Actualizar estado local
      setPruebas(pruebas.map(p => p.id === selectedPrueba.id ? response.data : p));
      setSelectedPrueba(response.data);
      setIsAddPreguntaDialogOpen(false);
      setEditingPreguntaId(null);
      
      // Reset form
      setNuevaPregunta({
        enunciado: '',
        tipo_respuesta: 'texto_largo',
        puntos: 10,
        criterios: '',
        video_youtube_url: '',
        imagen_url: '',
        pdf_url: '',
        opciones: [
          { letra: 'A', texto: '' },
          { letra: 'B', texto: '' },
          { letra: 'C', texto: '' },
          { letra: 'D', texto: '' }
        ],
        respuesta_correcta: '',
        feedback_correcto: '',
        feedback_incorrecto: ''
      });
      
      toast.success(editingPreguntaId ? '✅ Pregunta actualizada exitosamente' : '✅ Pregunta agregada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar pregunta');
    }
  };

  const handleAceptarPreguntasIA = async (preguntasIA) => {
    if (!selectedPrueba || !preguntasIA?.length) return;
    try {
      const ordenBase = selectedPrueba.preguntas?.length || 0;
      const nuevas = preguntasIA.map((p, i) => ({
        id: p.id,
        orden: ordenBase + i + 1,
        tipo: p.tipo,
        enunciado: p.enunciado,
        puntos: p.puntos || 5,
        tipo_respuesta: p.tipo_respuesta || (p.tipo === 'desarrollo' ? 'texto_largo' : null),
        criterios: p.criterios || '',
        video_youtube_url: null,
        imagen_url: null,
        opciones: p.tipo === 'multiple' ? p.opciones : null,
        respuesta_correcta: p.tipo === 'desarrollo' ? null : p.respuesta_correcta,
        feedback_correcto: '',
        feedback_incorrecto: '',
        generada_por_ia: true,
        referencia_ia: p.referencia_ia || ''
      }));
      const pruebaActualizada = {
        ...selectedPrueba,
        preguntas: [...(selectedPrueba.preguntas || []), ...nuevas],
        fecha_disponible: new Date(selectedPrueba.fecha_disponible).toISOString(),
        fecha_limite: new Date(selectedPrueba.fecha_limite).toISOString()
      };
      const response = await axios.put(`${API}/pruebas-examenes/${selectedPrueba.id}`, pruebaActualizada);
      setPruebas(pruebas.map(p => p.id === selectedPrueba.id ? response.data : p));
      setSelectedPrueba(response.data);
      toast.success(`✨ ${nuevas.length} pregunta(s) agregadas por Genesis AI`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar preguntas IA');
    }
  };


  const handleEliminarPregunta = async (preguntaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta pregunta?')) {
      return;
    }

    try {
      const pruebaActualizada = {
        ...selectedPrueba,
        preguntas: selectedPrueba.preguntas.filter(p => p.id !== preguntaId),
        fecha_disponible: new Date(selectedPrueba.fecha_disponible).toISOString(),
        fecha_limite: new Date(selectedPrueba.fecha_limite).toISOString()
      };

      const response = await axios.put(`${API}/pruebas-examenes/${selectedPrueba.id}`, pruebaActualizada);
      
      setPruebas(pruebas.map(p => p.id === selectedPrueba.id ? response.data : p));
      setSelectedPrueba(response.data);
      
      toast.success('✅ Pregunta eliminada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pregunta');
    }
  };

  const handleEditarPregunta = (pregunta) => {
    setEditingPreguntaId(pregunta.id);
    setTipoPreguntaNueva(pregunta.tipo);
    setNuevaPregunta({
      enunciado: pregunta.enunciado || '',
      tipo_respuesta: pregunta.tipo_respuesta || 'texto_largo',
      puntos: pregunta.puntos || 10,
      criterios: pregunta.criterios || '',
      video_youtube_url: pregunta.video_youtube_url || '',
      imagen_url: pregunta.imagen_url || '',
      pdf_url: pregunta.pdf_url || '',
      opciones: pregunta.opciones || [
        { letra: 'A', texto: '' },
        { letra: 'B', texto: '' },
        { letra: 'C', texto: '' },
        { letra: 'D', texto: '' }
      ],
      respuesta_correcta: pregunta.respuesta_correcta || '',
      feedback_correcto: pregunta.feedback_correcto || '',
      feedback_incorrecto: pregunta.feedback_incorrecto || '',
      etiqueta_izquierda: pregunta.etiqueta_izquierda || '',
      etiqueta_derecha: pregunta.etiqueta_derecha || '',
      es_review: !!pregunta.es_review
    });
    setIsAddPreguntaDialogOpen(true);
  };

  const handleAbrirGestionPreguntas = async (prueba) => {
    try {
      const response = await axios.get(`${API}/pruebas-examenes/${prueba.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const pruebaFresca = response.data;
      setPruebas(prev => prev.map(p => p.id === pruebaFresca.id ? pruebaFresca : p));
      setSelectedPrueba(pruebaFresca);
    } catch {
      setSelectedPrueba(prueba);
    }
    setIsPreguntasDialogOpen(true);
  };

  const handleVerificarEstadoPrueba = async (prueba) => {
    // Validar fechas antes de permitir acceso
    const ahora = new Date();
    const disponible = new Date(prueba.fecha_disponible);
    const limite = getLimiteEfectivo(prueba);
    
    if (ahora < disponible) {
      toast.error('Esta prueba aún no está disponible');
      return;
    }
    if (ahora > limite && !prueba.mi_estado_respuesta) {
      toast.error('El plazo para esta prueba ha vencido');
      return;
    }

    try {
      // Verificar si ya hay una respuesta del estudiante
      const response = await axios.get(`${API}/pruebas-examenes/mis-respuestas/${prueba.id}`);
      
      // Si hay respuesta
      if (response.data) {
        const miRespuesta = response.data;
        
        if (miRespuesta.estado === 'calificada') {
          // Mostrar resultados
          setSelectedPrueba(prueba);
          setMiRespuestaCalificada(miRespuesta);
          setIsVerResultadosEstudianteDialogOpen(true);
        } else if (miRespuesta.estado === 'calificando') {
          toast.info('Tu prueba ha sido enviada y está pendiente de calificación');
        } else {
          // En progreso - continuar
          handleComenzarPrueba(prueba);
        }
      }
    } catch (error) {
      // 404 significa que no ha comenzado
      if (error.response?.status === 404) {
        handleComenzarPrueba(prueba);
      } else {
        toast.error('Error al verificar estado: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleComenzarPrueba = async (prueba) => {
    try {
      console.log('🔍 Iniciando prueba:', prueba.titulo);
      console.log('🔍 Número de preguntas:', prueba.preguntas?.length);
      
      // Verificar que la prueba tenga preguntas
      if (!prueba.preguntas || prueba.preguntas.length === 0) {
        toast.error('Esta prueba no tiene preguntas configuradas. Por favor contacta al profesor.');
        return;
      }
      
      // Reordenar: Material de Apoyo SIEMPRE arriba
      const preguntasOrdenadas = ordenarPreguntasParaMostrar(prueba.preguntas);
      const pruebaOrdenada = { ...prueba, preguntas: preguntasOrdenadas };
      
      // Iniciar prueba en el backend
      const response = await axios.post(`${API}/pruebas-examenes/${prueba.id}/iniciar`);
      setRespuestaActual(response.data);
      setSelectedPrueba(pruebaOrdenada);
      
      // Inicializar respuestas vacías (en el mismo orden que se muestran)
      const respuestasIniciales = preguntasOrdenadas.map(p => ({
        pregunta_id: p.id,
        pregunta_orden: p.orden,
        respuesta_texto: '',
        respuesta_seleccionada: '',
        archivo_adjunto: null
      }));
      
      // AUTO-RESTORE: Check localStorage for saved answers
      const savedKey = `prueba_respuestas_${prueba.id}`;
      const saved = localStorage.getItem(savedKey);
      if (saved) {
        try {
          const savedData = JSON.parse(saved);
          let restored = 0;
          savedData.forEach(savedResp => {
            const idx = respuestasIniciales.findIndex(r => r.pregunta_id === savedResp.pregunta_id);
            if (idx !== -1) {
              if (savedResp.respuesta_texto) {
                respuestasIniciales[idx].respuesta_texto = savedResp.respuesta_texto;
                restored++;
              }
              if (savedResp.respuesta_seleccionada) {
                respuestasIniciales[idx].respuesta_seleccionada = savedResp.respuesta_seleccionada;
                restored++;
              }
            }
          });
          if (restored > 0) {
            toast.info(`Se recuperaron ${restored} respuesta(s) guardada(s)`, { duration: 4000 });
          }
        } catch (e) {}
      }
      
      setRespuestasEstudiante(respuestasIniciales);
      
      setIsTomandoPruebaDialogOpen(true);
      toast.success('¡Prueba iniciada! Buena suerte 🍀');
    } catch (error) {
      console.error('❌ Error en handleComenzarPrueba:', error);
      
      // Manejar errores específicos
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('completado esta prueba')) {
          toast.error('Ya has completado esta prueba.');
        } else if (detail.includes('aún no está disponible')) {
          toast.error('Esta prueba aún no está disponible.');
        } else if (detail.includes('fecha límite ha pasado')) {
          toast.error('La fecha límite para esta prueba ha pasado.');
        } else {
          toast.error(detail);
        }
      } else {
        toast.error('Error al iniciar prueba. Por favor intenta nuevamente.');
      }
    }
  };

  // Proteger contra cierre accidental de la prueba
  const getPreguntasPendientes = () => {
    if (!selectedPrueba?.preguntas || !respuestasEstudiante) return 0;
    const preguntasReales = selectedPrueba.preguntas.filter(p => !esNoCalificable(p.tipo));
    return preguntasReales.filter(p => {
      const resp = respuestasEstudiante.find(r => r.pregunta_id === p.id);
      if (!resp) return true;
      return !(resp.respuesta_texto?.trim() || resp.respuesta_seleccionada?.trim());
    }).length;
  };

  const handleCerrarPrueba = (open) => {
    if (open) {
      setIsTomandoPruebaDialogOpen(true);
      return;
    }
    // Intentando cerrar
    const pendientes = getPreguntasPendientes();
    const totalReales = (selectedPrueba?.preguntas || []).filter(p => !esNoCalificable(p.tipo)).length;
    const contestadas = totalReales - pendientes;

    if (contestadas > 0 || pendientes > 0) {
      const guardar = window.confirm(
        pendientes > 0
          ? `Tienes ${pendientes} de ${totalReales} pregunta(s) sin responder.\n\n¿Deseas guardar tu progreso para continuar después?\n\nPresiona OK para GUARDAR y salir.\nPresiona Cancelar para SEGUIR contestando.`
          : `¿Deseas guardar y salir?\n\nPresiona OK para GUARDAR y salir.\nPresiona Cancelar para SEGUIR contestando.`
      );
      if (guardar) {
        if (selectedPrueba && respuestasEstudiante.length > 0) {
          localStorage.setItem(`prueba_respuestas_${selectedPrueba.id}`, JSON.stringify(respuestasEstudiante));
        }
        setIsTomandoPruebaDialogOpen(false);
        toast.success('Respuestas guardadas. Puedes continuar luego.');
      }
      // Si cancela, simplemente no cierra
    } else {
      setIsTomandoPruebaDialogOpen(false);
    }
  };

  // Auto-guardar cada 30 segundos mientras la prueba está abierta
  useEffect(() => {
    if (!isTomandoPruebaDialogOpen || !selectedPrueba || !respuestasEstudiante.length) return;
    const interval = setInterval(() => {
      localStorage.setItem(`prueba_respuestas_${selectedPrueba.id}`, JSON.stringify(respuestasEstudiante));
    }, 30000);
    return () => clearInterval(interval);
  }, [isTomandoPruebaDialogOpen, selectedPrueba, respuestasEstudiante]);

  // Prevenir cierre de pestaña durante la prueba
  useEffect(() => {
    if (!isTomandoPruebaDialogOpen) return;
    const handler = (e) => {
      if (selectedPrueba && respuestasEstudiante.length > 0) {
        localStorage.setItem(`prueba_respuestas_${selectedPrueba.id}`, JSON.stringify(respuestasEstudiante));
      }
      e.preventDefault();
      e.returnValue = 'Tienes una prueba en progreso. Si sales, tus respuestas se guardarán automáticamente.';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isTomandoPruebaDialogOpen, selectedPrueba, respuestasEstudiante]);

  const handleEnviarPrueba = async () => {
    // Validar que al menos haya respuestas escritas (ignorar items no calificables)
    const preguntasReales = selectedPrueba?.preguntas?.filter(p => !esNoCalificable(p.tipo)) || [];
    const respuestasConContenido = respuestasEstudiante.filter(r => {
      const pregunta = preguntasReales.find(p => p.id === r.pregunta_id);
      if (!pregunta) return false;
      return (r.respuesta_texto && r.respuesta_texto.trim()) || (r.respuesta_seleccionada && r.respuesta_seleccionada.trim());
    });

    if (preguntasReales.length > 0 && respuestasConContenido.length === 0) {
      toast.error('No puedes enviar la prueba sin responder ninguna pregunta.');
      return;
    }

    const sinResponder = preguntasReales.length - respuestasConContenido.length;
    let mensajeConfirm = '¿Estás seguro de enviar la prueba? No podrás modificar tus respuestas después.';
    if (sinResponder > 0) {
      mensajeConfirm = `Tienes ${sinResponder} pregunta(s) sin responder. ¿Estás seguro de enviar la prueba así? No podrás modificar tus respuestas después.`;
    }

    if (!window.confirm(mensajeConfirm)) {
      return;
    }

    try {
      await axios.post(`${API}/pruebas-examenes/respuestas/enviar`, {
        respuesta_prueba_id: respuestaActual.id,
        respuestas: respuestasEstudiante
      });
      
      // Clear auto-saved answers after successful submission
      if (selectedPrueba) {
        localStorage.removeItem(`prueba_respuestas_${selectedPrueba.id}`);
      }
      
      setIsTomandoPruebaDialogOpen(false);
      toast.success('✅ ¡Prueba enviada exitosamente!');
      
      // Recargar pruebas
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar prueba');
    }
  };

  // Abrir el modal con histograma agregado de preguntas Review (escala_1_10 marcadas)
  const abrirReviewResultados = async (pruebaId) => {
    if (!pruebaId) return;
    setIsReviewResultadosOpen(true);
    setLoadingReviewResultados(true);
    setReviewResultados(null);
    try {
      const r = await axios.get(`${API}/pruebas-examenes/${pruebaId}/review-resultados`);
      setReviewResultados(r.data);
    } catch (e) {
      console.error('Error al cargar resultados Review:', e);
      toast.error('Error al cargar resultados Review: ' + (e.response?.data?.detail || e.message));
      setIsReviewResultadosOpen(false);
    } finally {
      setLoadingReviewResultados(false);
    }
  };


  const handleVerRespuestasProfesor = async (prueba) => {
    try {
      console.log('🔍 Cargando respuestas para prueba:', prueba.titulo);
      const response = await axios.get(`${API}/pruebas-examenes/${prueba.id}/respuestas`);
      console.log('📋 Respuestas recibidas:', response.data);
      
      setSelectedPrueba(prueba);
      setRespuestasRecibidas(response.data);
      
      // También cargar los que NO respondieron
      try {
        const noRespondieronResponse = await axios.get(`${API}/pruebas-examenes/${prueba.id}/no-respondieron`);
        setEstudiantesNoRespondieron(noRespondieronResponse.data.estudiantes_no_respondieron || []);
        setInfoNoRespondieron(noRespondieronResponse.data);
        console.log('📋 No respondieron:', noRespondieronResponse.data);
      } catch (err) {
        console.log('No se pudo cargar lista de no respondieron:', err);
        setEstudiantesNoRespondieron([]);
        setInfoNoRespondieron(null);
      }
      
      setIsRespuestasDialogOpen(true);
      
      if (response.data.length === 0) {
        toast.info('Aún no hay respuestas de estudiantes para esta prueba');
      }
    } catch (error) {
      console.error('Error al cargar respuestas:', error);
      toast.error('Error al cargar respuestas: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Función para calificar con 0 a los que no respondieron
  const handleCalificarNoRespondieron = async () => {
    if (!selectedPrueba) return;
    
    const sinCalificar = estudiantesNoRespondieron.filter(e => !e.ya_calificado_cero);
    if (sinCalificar.length === 0) {
      toast.info('Todos los estudiantes que no respondieron ya tienen calificación');
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de calificar con 0 a ${sinCalificar.length} estudiante(s) que no respondieron?\n\nSe les enviará una notificación informándoles.`)) {
      return;
    }
    
    setCalificandoNoRespondieron(true);
    try {
      const response = await axios.post(
        `${API}/pruebas-examenes/${selectedPrueba.id}/calificar-no-respondieron?calificacion=0&notificar=true`
      );
      
      toast.success(response.data.message);
      
      // Recargar la lista
      const noRespondieronResponse = await axios.get(`${API}/pruebas-examenes/${selectedPrueba.id}/no-respondieron`);
      setEstudiantesNoRespondieron(noRespondieronResponse.data.estudiantes_no_respondieron || []);
      setInfoNoRespondieron(noRespondieronResponse.data);
      
    } catch (error) {
      console.error('Error al calificar:', error);
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCalificandoNoRespondieron(false);
    }
  };

  const handleCalificarRespuesta = async (respuesta) => {
    setRespuestaSeleccionada(respuesta);
    
    // Inicializar calificaciones con las existentes o vacías
    const califs = {};
    respuesta.respuestas.forEach(resp => {
      califs[resp.pregunta_id] = {
        puntos: resp.puntos_obtenidos || 0,
        comentario: resp.comentario_profesor || ''
      };
    });
    setCalificaciones(califs);
    setIsCalificarDialogOpen(true);
  };

  const handleGuardarCalificacion = async () => {
    try {
      // Enviar cada calificación al backend
      for (const resp of respuestaSeleccionada.respuestas) {
        const calif = calificaciones[resp.pregunta_id];
        if (calif) {
          await axios.post(`${API}/pruebas-examenes/calificar-pregunta`, {
            respuesta_prueba_id: respuestaSeleccionada.id,
            pregunta_id: resp.pregunta_id,
            puntos_obtenidos: parseFloat(calif.puntos) || 0,
            comentario_profesor: calif.comentario || ''
          });
        }
      }
      
      toast.success('✅ Calificación guardada exitosamente');
      setIsCalificarDialogOpen(false);
      
      // Recargar respuestas
      handleVerRespuestasProfesor(selectedPrueba);
    } catch (error) {
      console.error('Error al calificar:', error);
      toast.error('Error al guardar calificación: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReenviarPrueba = async (respuesta) => {
    if (!window.confirm(`¿Estás seguro de reenviar la prueba a ${respuesta.estudiante_nombre}?\n\nSe eliminará su respuesta actual y podrá volver a realizarla. Se le notificará por email y push.`)) {
      return;
    }
    
    setReenviarLoading(true);
    try {
      const response = await axios.post(`${API}/pruebas-examenes/respuestas/${respuesta.id}/reenviar`);
      toast.success(response.data.message);
      setIsCalificarDialogOpen(false);
      
      // Recargar respuestas
      handleVerRespuestasProfesor(selectedPrueba);
    } catch (error) {
      console.error('Error al reenviar prueba:', error);
      toast.error('Error al reenviar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReenviarLoading(false);
    }
  };

  const handleReabrirPrueba = async (prueba) => {
    setReabrirPrueba(prueba);
    setReabrirSeleccionados([]);
    setReabrirFecha('');
    setReabrirLoading(false);
    try {
      // Obtener solo estudiantes que NO completaron la prueba
      const response = await axios.get(`${API}/pruebas-examenes/${prueba.id}/no-respondieron`);
      const noRespondieron = response.data.estudiantes_no_respondieron || [];
      setReabrirEstudiantes(noRespondieron);
    } catch (error) {
      console.error('Error al obtener estudiantes no respondieron:', error);
      setReabrirEstudiantes([]);
    }
    setIsReabrirDialogOpen(true);
  };

  const handleConfirmarReabrir = async () => {
    if (reabrirSeleccionados.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    if (!reabrirFecha) {
      toast.error('Selecciona una nueva fecha límite');
      return;
    }
    setReabrirLoading(true);
    try {
      const fechaLimite = new Date(reabrirFecha + 'T23:59:59');
      const response = await axios.post(`${API}/pruebas-examenes/${reabrirPrueba.id}/reabrir`, {
        estudiante_ids: reabrirSeleccionados,
        nueva_fecha_limite: fechaLimite.toISOString()
      });
      toast.success(response.data.message);
      setIsReabrirDialogOpen(false);
      // Refrescar la lista para que la prueba aparezca en "Pendientes"
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reabrir prueba');
    } finally {
      setReabrirLoading(false);
    }
  };

  const toggleReabrirEstudiante = (id) => {
    setReabrirSeleccionados(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleReabrirTodos = () => {
    if (reabrirSeleccionados.length === reabrirEstudiantes.length) {
      setReabrirSeleccionados([]);
    } else {
      setReabrirSeleccionados(reabrirEstudiantes.map(e => e.id));
    }
  };

  const handleCrearPrueba = async (e) => {
    e.preventDefault();
    
    if (!newPrueba.clase_id || !newPrueba.titulo || !newPrueba.fecha_disponible || !newPrueba.fecha_limite) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      // Convertir fechas: fecha_disponible a 00:00, fecha_limite a 23:59
      const fechaDisponible = new Date(newPrueba.fecha_disponible + 'T00:00:00');
      const fechaLimite = new Date(newPrueba.fecha_limite + 'T23:59:59');
      
      const dataToSend = {
        ...newPrueba,
        fecha_disponible: fechaDisponible.toISOString(),
        fecha_limite: fechaLimite.toISOString()
      };

      const response = await axios.post(`${API}/pruebas-examenes`, dataToSend);
      setPruebas([response.data, ...pruebas]);
      setIsCreateDialogOpen(false);
      
      toast.success('✅ Prueba/Examen creado y notificaciones enviadas');
      
      // Si es preguntas del sistema, abrir diálogo para agregar preguntas
      if (newPrueba.modalidad === 'preguntas_sistema') {
        setSelectedPrueba(response.data);
        setIsPreguntasDialogOpen(true);
      }
      
      // Reset form
      setNewPrueba({
        clase_id: '',
        titulo: '',
        descripcion: '',
        tipo: 'quiz',
        modalidad: 'preguntas_sistema',
        link_externo: '',
        fecha_disponible: '',
        fecha_limite: '',
        duracion_minutos: 0,
        puntos_totales: 100,
        peso_nota_final: 20,
        temario: '',
        calificar_automatico_cero: true,
        es_review_prueba: false,
        es_prueba_final_capacitacion: false,
        config: {
          mostrar_resultados_inmediato: true,
          permitir_archivos: true,
          prevenir_copy_paste: true,
          barajar_preguntas: false
        },
        preguntas: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear prueba');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando pruebas y exámenes...</p>
        </div>
      </div>
    );
  }

  // Separar pruebas por estado
  // Filtrar por clase si viene el parámetro Y por estado de clase (activa/inactiva)
  const pruebasFiltradas = pruebas.filter(p => {
    // Filtro por clase seleccionada
    if (claseSeleccionada && p.clase_id !== claseSeleccionada) return false;
    
    // Filtro por estado de la clase (activa/inactiva)
    if (filtroEstadoClase !== 'todas') {
      const clase = clases.find(c => c.id === p.clase_id);
      if (!clase) return false;
      
      if (filtroEstadoClase === 'activas' && !clase.activa) return false;
      if (filtroEstadoClase === 'inactivas' && clase.activa) return false;
    }
    
    return true;
  });

  const pruebasPendientes = pruebasFiltradas.filter(p => {
    const ahora = new Date();
    const limite = getLimiteEfectivo(p);
    return ahora <= limite;
  });

  const pruebasVencidas = pruebasFiltradas.filter(p => {
    const ahora = new Date();
    const limite = getLimiteEfectivo(p);
    return ahora > limite;
  });

  return (
    <div className={embedded ? "" : "min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 sm:p-6"}>
      <div className={embedded ? "" : "max-w-7xl mx-auto"}>
        {/* Header */}
        {!embedded && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
                <i className="fas fa-file-alt" />
                Pruebas y Exámenes
              </h1>
              {claseSeleccionada && (() => {
                const clase = clases.find(c => c.id === claseSeleccionada);
                return (
                  <div className="mt-2 inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-lg border-2 border-purple-300">
                    <i className="fas fa-filter" />
                    <span className="font-semibold">Clase: {clase?.titulo}</span>
                    <button
                      onClick={() => {
                        setClaseSeleccionada(null);
                        window.history.pushState({}, '', '/app/pruebas-examenes');
                      }}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                      title="Quitar filtro"
                    >
                      <i className="fas fa-times-circle" />
                    </button>
                  </div>
                );
              })()}
              <p className="text-gray-600 mt-1">
                {claseSeleccionada 
                  ? 'Viendo pruebas de esta clase específica'
                  : user.rol === 'estudiante' 
                  ? 'Evaluaciones de tus clases inscritas'
                  : user.rol === 'profesor'
                  ? 'Gestiona las evaluaciones de tus clases'
                  : 'Vista administrativa de todas las evaluaciones'
                }
              </p>
            </div>

            {(() => {
              // Admin siempre puede crear
              if (user.rol === 'admin') return true;
              
              // Profesor solo puede crear si NO hay clase seleccionada (vista general)
              // O si ES el profesor titular de la clase seleccionada
              if (user.rol === 'profesor') {
                if (!claseSeleccionada) return true; // Vista general, puede crear
                
                // Hay clase seleccionada, verificar si es el profesor titular
                const clase = clases.find(c => c.id === claseSeleccionada);
                return clase?.profesor_id === user.id;
              }
              
              return false;
            })() && (
              <Button
                onClick={() => {
                  // Pre-llenar la clase seleccionada
                  setNewPrueba({
                    ...newPrueba,
                    clase_id: claseSeleccionada || ''
                  });
                  setIsCreateDialogOpen(true);
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg"
              >
                <i className="fas fa-plus mr-2" />
                Nueva Prueba/Examen
              </Button>
            )}
          </div>

          {/* Filtros por estado de clase */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="filtros-pruebas">
            <span className="text-sm text-gray-600 flex items-center">
              <i className="fas fa-filter mr-2"></i>
              Filtrar por clases:
            </span>
            <Button
              size="sm"
              variant={filtroEstadoClase === 'todas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstadoClase('todas')}
              className={filtroEstadoClase === 'todas' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              Todas
            </Button>
            <Button
              size="sm"
              variant={filtroEstadoClase === 'activas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstadoClase('activas')}
              className={filtroEstadoClase === 'activas' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <i className="fas fa-check-circle mr-1"></i>
              Activas
            </Button>
            <Button
              size="sm"
              variant={filtroEstadoClase === 'inactivas' ? 'default' : 'outline'}
              onClick={() => setFiltroEstadoClase('inactivas')}
              className={filtroEstadoClase === 'inactivas' ? 'bg-gray-600 hover:bg-gray-700' : ''}
            >
              <i className="fas fa-times-circle mr-1"></i>
              Inactivas
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-blue-600">{pruebasPendientes.length}</div>
              <div className="text-xs text-blue-700">Activas</div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-green-600">{pruebasVencidas.length}</div>
              <div className="text-xs text-green-700">Vencidas</div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-purple-600">{pruebas.length}</div>
              <div className="text-xs text-purple-700">Total</div>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4">
              <div className="text-2xl font-bold text-orange-600">{clases.length}</div>
              <div className="text-xs text-orange-700">Clases</div>
            </div>
          </div>
        </div>
        )}

        {/* Lista de Pruebas Pendientes */}
        {pruebasPendientes.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="fas fa-clock text-yellow-500" />
              Pendientes ({pruebasPendientes.length})
            </h2>

            {/* Resumen por clase - solo estudiantes */}
            {user.rol === 'estudiante' && (() => {
              const porClase = {};
              pruebasPendientes.forEach(p => {
                const ahora = new Date();
                const disponible = new Date(p.fecha_disponible);
                const limite = new Date(p.fecha_limite);
                const disponibleAhora = ahora >= disponible && ahora <= limite;
                if (!porClase[p.clase_id]) {
                  porClase[p.clase_id] = { nombre: p.clase_nombre, banner: p.clase_banner_url, total: 0, disponibles: 0 };
                }
                porClase[p.clase_id].total++;
                if (disponibleAhora && !p.mi_estado_respuesta) porClase[p.clase_id].disponibles++;
              });
              const clasesConPruebas = Object.entries(porClase);
              if (clasesConPruebas.length <= 1) return null;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="resumen-clases-pendientes">
                  {clasesConPruebas.map(([claseId, info]) => (
                    <div
                      key={claseId}
                      onClick={() => {
                        setClaseSeleccionada(claseId);
                        window.history.pushState({}, '', `/app/pruebas-examenes?clase=${claseId}`);
                      }}
                      className={`relative rounded-xl overflow-hidden cursor-pointer group transition-all duration-300 hover:shadow-lg ${info.disponibles > 0 ? 'ring-2 ring-orange-400 ring-offset-1' : ''}`}
                    >
                      <div className="h-20" style={getBannerStyle(info.banner)}>
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center p-2">
                          <span className="text-white font-bold text-sm text-center drop-shadow-lg line-clamp-2">{info.nombre}</span>
                        </div>
                      </div>
                      {info.disponibles > 0 && (
                        <div className="absolute -top-1 -right-1 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-lg animate-pulse">
                          {info.disponibles}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="space-y-4">
              {pruebasPendientes.map(prueba => (
                <Card key={prueba.id} data-prueba-card={prueba.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-300">
                  {/* Banner de la clase */}
                  <div 
                    className="h-32 w-full relative"
                    style={getBannerStyle(prueba.clase_banner_url)}
                  >
                    <div className="absolute inset-0 flex items-center justify-between p-4">
                      <div>
                        <h3 className="text-white text-2xl font-bold drop-shadow-lg">
                          {prueba.clase_nombre}
                        </h3>
                        <p className="text-white/90 text-sm drop-shadow">
                          <i className="fas fa-user mr-1" />
                          {prueba.profesor_nombre}
                        </p>
                      </div>
                      {getEstadoBadge(prueba)}
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          {prueba.titulo}
                        </h4>
                        {prueba.descripcion && (
                          <p className="text-sm text-gray-600 mb-3">{prueba.descripcion}</p>
                        )}
                      </div>
                      <Badge className={
                        prueba.tipo === 'quiz' ? 'bg-blue-500' :
                        prueba.tipo === 'examen_parcial' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }>
                        {prueba.tipo === 'quiz' ? '📝 Quiz' :
                         prueba.tipo === 'examen_parcial' ? '📋 Parcial' :
                         '🎯 Final'}
                      </Badge>
                      {prueba.es_review_prueba && (
                        <Badge className="bg-purple-600 ml-1" data-testid="badge-review-prueba">
                          📊 Review · no califica
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600">Disponible</div>
                        <div className="text-sm font-semibold text-gray-800">
                          {new Date(prueba.fecha_disponible).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600">Vence</div>
                        <div className="text-sm font-semibold text-red-600">
                          {new Date(prueba.fecha_limite).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600">Duración</div>
                        <div className="text-sm font-semibold text-gray-800">
                          Sin límite de tiempo
                        </div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600">Puntos</div>
                        <div className="text-sm font-semibold text-indigo-600">
                          {prueba.puntos_totales} pts ({prueba.peso_nota_final}%)
                        </div>
                      </div>
                    </div>

                    {prueba.modalidad === 'link_externo' ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-sm text-yellow-800">
                          <i className="fas fa-external-link-alt" />
                          <span>Formulario externo (Google Classroom u otro)</span>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-sm text-blue-800">
                          <i className="fas fa-clipboard-list" />
                          <span>{prueba.preguntas.length} preguntas en el sistema</span>
                        </div>
                      </div>
                    )}

                    {prueba.temario && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
                        <div className="text-xs font-semibold text-indigo-800 mb-1">
                          <i className="fas fa-book mr-1" />
                          Temario:
                        </div>
                        <div className="text-sm text-indigo-700">{prueba.temario}</div>
                      </div>
                    )}

                    {/* Botones - Layout VERTICAL para móvil */}
                    <div className="flex flex-col gap-2">
                      {/* Botón Ver Detalle - siempre visible */}
                      <Button
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                        onClick={async () => {
                          setSelectedPrueba(prueba);
                          setMisRespuestasDetalle(null);
                          setIsDetailDialogOpen(true);
                          // Intentar cargar tus respuestas calificadas (si no respondiste, el endpoint devuelve 404 y se ignora)
                          setLoadingMisRespuestas(true);
                          try {
                            const r = await axios.get(`${API}/pruebas-examenes/mis-respuestas/${prueba.id}`);
                            setMisRespuestasDetalle(r.data);
                          } catch (e) {
                            // 404 = usuario no respondió esta prueba. No mostrar sección.
                          } finally {
                            setLoadingMisRespuestas(false);
                          }
                        }}
                      >
                        <i className="fas fa-eye mr-2" />
                        Ver Detalle
                      </Button>
                      
                      {/* Botones para estudiantes */}
                      {user.rol === 'estudiante' && prueba.modalidad === 'preguntas_sistema' && (() => {
                        const btnInfo = getBotonEstudianteInfo(prueba);
                        return (
                          <Button 
                            className={`w-full ${btnInfo.clase} text-lg font-bold py-6`}
                            onClick={() => handleVerificarEstadoPrueba(prueba)}
                            disabled={btnInfo.disabled}
                          >
                            <i className={`fas ${btnInfo.icono} mr-2`} />
                            {btnInfo.texto}
                          </Button>
                        );
                      })()}

                      {user.rol === 'estudiante' && prueba.modalidad === 'link_externo' && prueba.link_externo && (
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700 text-lg font-bold py-6"
                          onClick={() => window.open(prueba.link_externo, '_blank')}
                        >
                          <i className="fas fa-external-link-alt mr-2" />
                          Abrir en Classroom
                        </Button>
                      )}

                      {/* Botones para profesor/admin */}
                      {(user.rol === 'admin' || user.rol === 'profesor') && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {/* Admin, profesor CREADOR de la prueba, o profesor ASIGNADO a la clase
                              (para Auto-Capacitación: admin crea la prueba pero el profesor de la
                              clase debe poder editarla) */}
                          {(user.rol === 'admin' || prueba.profesor_id === user.id || prueba.clase_profesor_id === user.id) ? (
                            <>
                              {prueba.modalidad === 'preguntas_sistema' && (
                                <>
                                  <Button
                                    className="w-full bg-purple-600 hover:bg-purple-700"
                                    onClick={() => handleVerRespuestasProfesor(prueba)}
                                  >
                                    <i className="fas fa-clipboard-check mr-2" />
                                    Ver Respuestas
                                  </Button>
                                  <Button
                                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                                    onClick={() => handleAbrirGestionPreguntas(prueba)}
                                  >
                                    <i className="fas fa-list-ol mr-2" />
                                    Preguntas ({prueba.preguntas.length})
                                  </Button>
                                </>
                              )}
                              <Button
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                onClick={() => handleEditarPrueba(prueba)}
                              >
                                <i className="fas fa-edit mr-2" />
                                Editar
                              </Button>
                              <Button
                                className="w-full bg-red-600 hover:bg-red-700"
                                onClick={() => handleEliminarPrueba(prueba.id)}
                              >
                                <i className="fas fa-trash mr-2" />
                                Borrar
                              </Button>
                            </>
                          ) : (
                            /* Profesor inscrito como ESTUDIANTE: solo puede contestar/ver */
                            (() => {
                              const btnInfo = getBotonEstudianteInfo(prueba);
                              return (
                                <Button
                                  className={`w-full ${btnInfo.clase}`}
                                  onClick={() => handleVerificarEstadoPrueba(prueba)}
                                >
                                  <i className={`fas ${btnInfo.icono} mr-2`} />
                                  {btnInfo.texto}
                                </Button>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Lista de Pruebas Vencidas/Completadas */}
        {pruebasVencidas.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="fas fa-check-circle text-green-500" />
              Vencidas/Completadas ({pruebasVencidas.length})
            </h2>
            <div className="space-y-4">
              {pruebasVencidas.map(prueba => (
                <Card key={prueba.id} data-prueba-card={prueba.id} className="overflow-hidden opacity-75 hover:opacity-100 transition-opacity">
                  <div 
                    className="h-20 w-full relative"
                    style={getBannerStyle(prueba.clase_banner_url)}
                  >
                    <div className="absolute inset-0 flex items-center justify-between p-4">
                      <h3 className="text-white text-lg font-bold drop-shadow-lg">
                        {prueba.clase_nombre} - {prueba.titulo}
                      </h3>
                      <Badge className="bg-gray-500">✅ Vencida</Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="text-sm text-gray-600">
                        Venció: {new Date(prueba.fecha_limite).toLocaleDateString('es-ES')}
                      </div>
                      
                      {/* Botones */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={async () => {
                            setSelectedPrueba(prueba);
                            setMisRespuestasDetalle(null);
                            setIsDetailDialogOpen(true);
                            setLoadingMisRespuestas(true);
                            try {
                              const r = await axios.get(`${API}/pruebas-examenes/mis-respuestas/${prueba.id}`);
                              setMisRespuestasDetalle(r.data);
                            } catch (e) {
                              // 404 = usuario no respondió
                            } finally {
                              setLoadingMisRespuestas(false);
                            }
                          }}
                        >
                          <i className="fas fa-eye mr-2" />
                          Ver Detalle
                        </Button>
                        
                        {(user.rol === 'admin' || user.rol === 'profesor') && (
                          <>
                            {/* Admin, profesor CREADOR, o profesor ASIGNADO a la clase
                                (para Auto-Capacitación: admin crea pero profesor edita) */}
                            {(user.rol === 'admin' || prueba.profesor_id === user.id || prueba.clase_profesor_id === user.id) ? (
                              <>
                                {prueba.modalidad === 'preguntas_sistema' && (
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                                    onClick={() => handleVerRespuestasProfesor(prueba)}
                                  >
                                    <i className="fas fa-clipboard-check mr-2" />
                                    Ver Respuestas
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                  onClick={() => handleEditarPrueba(prueba)}
                                >
                                  <i className="fas fa-edit mr-2" />
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => handleEliminarPrueba(prueba.id)}
                                >
                                  <i className="fas fa-trash mr-2" />
                                  Borrar
                                </Button>
                                <Button
                                  size="sm"
                                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                                  onClick={() => handleReabrirPrueba(prueba)}
                                  data-testid="reabrir-prueba-btn"
                                >
                                  <i className="fas fa-lock-open mr-2" />
                                  Reabrir Prueba
                                </Button>
                              </>
                            ) : (
                              /* Profesor inscrito como ESTUDIANTE: solo puede ver resultados */
                              (() => {
                                const btnInfo = getBotonEstudianteInfo(prueba);
                                return (
                                  <Button
                                    size="sm"
                                    className={`flex-1 ${btnInfo.clase} text-white`}
                                    onClick={() => handleVerificarEstadoPrueba(prueba)}
                                    disabled={btnInfo.disabled}
                                  >
                                    <i className={`fas ${btnInfo.icono} mr-2`} />
                                    {btnInfo.texto}
                                  </Button>
                                );
                              })()
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {pruebas.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">
              No hay pruebas o exámenes
            </h3>
            <p className="text-gray-500">
              {user.rol === 'estudiante' 
                ? 'Tus profesores aún no han creado evaluaciones'
                : 'Comienza creando tu primera prueba o examen'
              }
            </p>
          </div>
        )}
      </div>

      {/* Dialog Crear Prueba/Examen */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) {
          // Reset form when closing
          setNewPrueba({
            clase_id: '',
            titulo: '',
            descripcion: '',
            tipo: 'quiz',
            modalidad: 'preguntas_sistema',
            link_externo: '',
            fecha_disponible: '',
            fecha_limite: '',
            zona_horaria: 'America/Montevideo',
            duracion_minutos: 0,
            puntos_totales: 100,
            peso_nota_final: 20,
            temario: '',
            calificar_automatico_cero: true,
            es_review_prueba: false,
            es_prueba_final_capacitacion: false,
            config: {
              mostrar_resultados_inmediato: true,
              permitir_archivos: true,
              prevenir_copy_paste: true,
              barajar_preguntas: false
            },
            preguntas: []
          });
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-indigo-50 to-purple-50 border-4 border-indigo-200 shadow-2xl">
          <DialogHeader className="border-b-4 border-indigo-200 pb-4 mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 -m-6 mb-6 p-6 rounded-t-lg">
            <DialogTitle className="text-3xl font-black text-white flex items-center gap-3 drop-shadow-lg">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <i className="fas fa-plus-circle text-3xl text-indigo-600" />
              </div>
              Crear Nueva Prueba/Examen
            </DialogTitle>
            <p className="text-indigo-100 mt-2 text-sm">Completa la información para crear una nueva evaluación</p>
          </DialogHeader>

          <form onSubmit={handleCrearPrueba} className="space-y-6">
            {/* Clase */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Clase * <span className="text-xs text-gray-500">(Los estudiantes de esta clase recibirán la prueba)</span>
              </Label>
              <Select
                value={newPrueba.clase_id}
                onValueChange={(value) => setNewPrueba({...newPrueba, clase_id: value})}
              >
                <SelectTrigger className="border-2 border-indigo-200">
                  <SelectValue placeholder="Selecciona una clase" />
                </SelectTrigger>
                <SelectContent>
                  {clases.map(clase => (
                    <SelectItem key={clase.id} value={clase.id}>
                      {clase.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Mostrar banner de la clase seleccionada */}
              {newPrueba.clase_id && (() => {
                const claseSeleccionada = clases.find(c => c.id === newPrueba.clase_id);
                if (claseSeleccionada) {
                  return (
                    <div className="mt-3 rounded-xl overflow-hidden border-2 border-indigo-300 shadow-lg">
                      <div 
                        className="h-24 w-full relative"
                        style={getBannerStyle(claseSeleccionada.banner_url)}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <h3 className="text-white text-xl font-bold drop-shadow-lg">
                            {claseSeleccionada.titulo}
                          </h3>
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Título */}
            <div>
              <Label>Título de la Prueba/Examen *</Label>
              <Input
                value={newPrueba.titulo}
                onChange={(e) => setNewPrueba({...newPrueba, titulo: e.target.value})}
                placeholder="Ej: Examen Parcial - Unidad 1"
                className="border-2 border-indigo-200"
              />
            </div>

            {/* Descripción */}
            <div>
              <Label>Descripción / Instrucciones</Label>
              <Textarea
                value={newPrueba.descripcion}
                onChange={(e) => setNewPrueba({...newPrueba, descripcion: e.target.value})}
                placeholder="Instrucciones para el estudiante..."
                rows={3}
                className="border-2 border-indigo-200"
              />
            </div>

            {/* Tipo */}
            <div>
              <Label>Tipo de Evaluación *</Label>
              <Select
                value={newPrueba.tipo}
                onValueChange={(value) => setNewPrueba({...newPrueba, tipo: value})}
              >
                <SelectTrigger className="border-2 border-indigo-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quiz">📝 Quiz/Prueba Rápida</SelectItem>
                  <SelectItem value="examen_parcial">📋 Examen Parcial</SelectItem>
                  <SelectItem value="examen_final">🎯 Examen Final</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Modalidad */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50 py-3">
                <CardTitle className="text-base">Modalidad de Evaluación</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={newPrueba.modalidad === 'preguntas_sistema'}
                      onChange={() => setNewPrueba({...newPrueba, modalidad: 'preguntas_sistema'})}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold">📝 Preguntas en el Sistema</div>
                      <div className="text-xs text-gray-600">
                        Crea las preguntas aquí (desarrollo, múltiple opción, V/F)
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={newPrueba.modalidad === 'link_externo'}
                      onChange={() => setNewPrueba({...newPrueba, modalidad: 'link_externo'})}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold">🔗 Formulario Externo</div>
                      <div className="text-xs text-gray-600">
                        Google Classroom, Forms u otra plataforma
                      </div>
                    </div>
                  </label>

                  {newPrueba.modalidad === 'link_externo' && (
                    <Input
                      value={newPrueba.link_externo}
                      onChange={(e) => setNewPrueba({...newPrueba, link_externo: e.target.value})}
                      placeholder="https://classroom.google.com/..."
                      className="mt-2"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Fechas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <i className="fas fa-calendar-check text-green-600" />
                  Fecha Disponible *
                </Label>
                <Input
                  type="date"
                  value={newPrueba.fecha_disponible}
                  onChange={(e) => setNewPrueba({...newPrueba, fecha_disponible: e.target.value})}
                  className="border-2 border-indigo-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La prueba estará disponible desde las 00:00 de este día
                </p>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <i className="fas fa-calendar-times text-red-600" />
                  Fecha Límite *
                </Label>
                <Input
                  type="date"
                  value={newPrueba.fecha_limite}
                  onChange={(e) => setNewPrueba({...newPrueba, fecha_limite: e.target.value})}
                  className="border-2 border-indigo-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La prueba se cierra a las 23:59 de este día
                </p>
              </div>
            </div>

            {/* Puntos */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Puntos Totales *</Label>
                <Input
                  type="number"
                  value={newPrueba.puntos_totales}
                  onChange={(e) => setNewPrueba({...newPrueba, puntos_totales: parseInt(e.target.value)})}
                  className="border-2 border-indigo-200"
                />
              </div>
              <div>
                <Label>Peso en Nota (%)</Label>
                <Input
                  type="number"
                  value={newPrueba.peso_nota_final}
                  onChange={(e) => setNewPrueba({...newPrueba, peso_nota_final: parseInt(e.target.value)})}
                  className="border-2 border-indigo-200"
                />
              </div>
            </div>

            {/* Temario */}
            <div>
              <Label>Temario / Material de Estudio</Label>
              <Textarea
                value={newPrueba.temario}
                onChange={(e) => setNewPrueba({...newPrueba, temario: e.target.value})}
                placeholder="Capítulos 1-3, páginas 15-50..."
                rows={2}
                className="border-2 border-indigo-200"
              />
            </div>

            {/* Opciones */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50 py-3">
                <CardTitle className="text-base">⚙️ Opciones Adicionales</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPrueba.config.mostrar_resultados_inmediato}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      config: {...newPrueba.config, mostrar_resultados_inmediato: e.target.checked}
                    })}
                  />
                  <span className="text-sm">Mostrar resultados inmediatamente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPrueba.config.permitir_archivos}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      config: {...newPrueba.config, permitir_archivos: e.target.checked}
                    })}
                  />
                  <span className="text-sm">Permitir adjuntar archivos en respuestas</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPrueba.config.prevenir_copy_paste}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      config: {...newPrueba.config, prevenir_copy_paste: e.target.checked}
                    })}
                  />
                  <span className="text-sm">Prevenir copiar/pegar</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPrueba.config.barajar_preguntas}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      config: {...newPrueba.config, barajar_preguntas: e.target.checked}
                    })}
                  />
                  <span className="text-sm">Barajar preguntas (orden aleatorio)</span>
                </label>
              </CardContent>
            </Card>

            {/* Calificación automática */}
            <Card className="border-2 border-red-200 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                  <i className="fas fa-clock text-red-600" />
                  Calificación al Vencer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPrueba.calificar_automatico_cero}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      calificar_automatico_cero: e.target.checked
                    })}
                    className="mt-1 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-red-800">Calificar automáticamente con 0 al vencer</span>
                    <p className="text-xs text-red-600 mt-1">
                      Si está marcado, los estudiantes que no respondan antes de la fecha límite recibirán automáticamente 0 puntos y una notificación.
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Toggle Review/Encuesta — no afecta promedio */}
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                  <i className="fas fa-chart-column text-purple-600" />
                  Tipo de Prueba
                </CardTitle>
              </CardHeader>
              <CardContent>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="label-es-review-prueba">
                  <input
                    type="checkbox"
                    data-testid="check-es-review-prueba"
                    checked={!!newPrueba.es_review_prueba}
                    onChange={(e) => setNewPrueba({
                      ...newPrueba,
                      es_review_prueba: e.target.checked,
                      // Si es review, no tiene sentido calificar con 0
                      calificar_automatico_cero: e.target.checked ? false : newPrueba.calificar_automatico_cero,
                    })}
                    className="mt-1 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-purple-800">Es Review / Encuesta (no califica)</span>
                    <p className="text-xs text-purple-700 mt-1">
                      Marcá esto si la prueba es solo para feedback (escala 1-10, opinión, etc.). <strong>No aparecerá en Calificaciones ni afectará el promedio</strong> de la clase, aunque el alumno la responda o no.
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Toggle Prueba final de Auto-Capacitación
                Solo aparece si la clase seleccionada es autocalificable.
                Cuando un profesor con capacitado=false entrega esta prueba, el
                sistema lo desbloquea automáticamente y notifica al admin. */}
            {(() => {
              const claseSel = clases.find(c => c.id === newPrueba.clase_id);
              const esAutoCap = claseSel && (claseSel.categoria === 'autocapacitacion' || claseSel.categoria === 'autocapacitacion_profesores' || claseSel.es_autocapacitacion === true);
              if (!esAutoCap) return null;
              return (
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                      <i className="fas fa-flag-checkered text-emerald-600" />
                      Prueba final del curso (Auto-Capacitación)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="label-es-prueba-final-cap">
                      <input
                        type="checkbox"
                        data-testid="check-es-prueba-final-cap"
                        checked={!!newPrueba.es_prueba_final_capacitacion}
                        onChange={(e) => setNewPrueba({
                          ...newPrueba,
                          es_prueba_final_capacitacion: e.target.checked,
                        })}
                        className="mt-1 w-5 h-5"
                      />
                      <div>
                        <span className="text-sm font-medium text-emerald-800">
                          Esta prueba finaliza la auto-capacitación
                        </span>
                        <p className="text-xs text-emerald-700 mt-1">
                          Marcá esta opción solo en la <strong>última prueba</strong> del curso.
                          Cuando un profesor no calificado la entregue, su cuenta se
                          <strong> habilitará automáticamente</strong> (deja de estar bloqueado)
                          y recibirá una notificación. Vos también recibirás un aviso por push y email.
                        </p>
                      </div>
                    </label>
                  </CardContent>
                </Card>
              );
            })()}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-800">
                <i className="fas fa-info-circle mr-2" />
                Después de crear, podrás agregar las preguntas si elegiste "Preguntas en el Sistema"
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
              >
                <i className="fas fa-check mr-2" />
                Crear Prueba/Examen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle - Muestra respuestas calificadas para estudiantes */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Prueba/Examen</DialogTitle>
          </DialogHeader>
          {selectedPrueba && (
            <div className="space-y-4">
              <div 
                className="h-32 w-full rounded-lg relative"
                style={getBannerStyle(selectedPrueba.clase_banner_url)}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <h3 className="text-white text-2xl font-bold drop-shadow-lg">
                    {selectedPrueba.clase_nombre}
                  </h3>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold">{selectedPrueba.titulo}</h3>
                <p className="text-gray-600">{selectedPrueba.descripcion}</p>
              </div>
              <div className="text-sm text-gray-600">
                <p>Disponible: {new Date(selectedPrueba.fecha_disponible).toLocaleString('es-ES')}</p>
                <p>Vence: {new Date(selectedPrueba.fecha_limite).toLocaleString('es-ES')}</p>
                <p>Puntos: {selectedPrueba.puntos_totales} ({selectedPrueba.peso_nota_final}%)</p>
              </div>
              {selectedPrueba.modalidad === 'preguntas_sistema' && (
                <div>
                  <p className="font-semibold">Preguntas: {selectedPrueba.preguntas.length}</p>
                </div>
              )}

              {/* Ver Resultados Review — solo profesor/admin si hay al menos 1 pregunta es_review */}
              {(user.rol === 'admin' || user.rol === 'profesor') &&
                (selectedPrueba.preguntas || []).some((p) => p.tipo === 'escala_1_10' && p.es_review) && (
                  <div className="border-t pt-4 mt-2">
                    <Button
                      onClick={() => abrirReviewResultados(selectedPrueba.id)}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                      data-testid="btn-ver-review-resultados"
                    >
                      <i className="fas fa-chart-column mr-2" />
                      Ver Resultados Review
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1.5 italic">
                      Visible solo para profesores y admin. Muestra cuántos estudiantes pusieron cada nota.
                    </p>
                  </div>
                )}

              {/* Sección de respuestas calificadas (propias del usuario) */}
              {(loadingMisRespuestas || misRespuestasDetalle) && (
                <div className="border-t pt-4 mt-4">
                  {loadingMisRespuestas ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="fas fa-spinner fa-spin text-2xl mb-2" />
                      <p>Cargando tus respuestas...</p>
                    </div>
                  ) : misRespuestasDetalle ? (
                    <div className="space-y-4" data-testid="mis-respuestas-detalle">
                      {/* Resumen de calificación */}
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-xs text-indigo-700 uppercase tracking-wide font-semibold">Tu calificación</p>
                            <p className="text-3xl font-bold text-indigo-800">
                              {misRespuestasDetalle.puntos_totales_obtenidos != null ? misRespuestasDetalle.puntos_totales_obtenidos : '-'}
                              <span className="text-lg text-gray-500"> / {selectedPrueba.puntos_totales} pts</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              misRespuestasDetalle.estado === 'calificada' ? 'bg-green-500' :
                              misRespuestasDetalle.estado === 'calificando' ? 'bg-amber-500' :
                              'bg-gray-500'
                            }>
                              {misRespuestasDetalle.estado === 'calificada' ? '✅ Calificada' :
                               misRespuestasDetalle.estado === 'calificando' ? '⏳ En revisión' :
                               '📝 Respondida'}
                            </Badge>
                            {misRespuestasDetalle.calificacion_final != null && (
                              <p className="text-sm text-indigo-700 mt-1 font-semibold">
                                {misRespuestasDetalle.calificacion_final.toFixed(1)}%
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 🎓 Descargar diploma — solo si es prueba final de autocapacitación y aprobó con ≥70 */}
                      {selectedPrueba?.es_prueba_final_capacitacion &&
                       misRespuestasDetalle.calificacion_final != null &&
                       misRespuestasDetalle.calificacion_final >= 70 && (
                        <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-100 border-2 border-amber-300">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1">
                              <h4 className="font-bold text-amber-900 flex items-center gap-2">
                                <i className="fas fa-award text-amber-600 text-xl" />
                                ¡Felicitaciones! Diploma emitido
                              </h4>
                              <p className="text-xs text-amber-800 mt-1">
                                Completaste la autocapacitación con la nota mínima requerida. Descargá tu diploma oficial en PDF.
                              </p>
                            </div>
                            <Button
                              className="bg-amber-600 hover:bg-amber-700 text-white shadow"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const resp = await fetch(`${API}/diplomas/mi-autocapacitacion/${selectedPrueba.clase_id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (!resp.ok) {
                                    const err = await resp.json().catch(() => ({}));
                                    toast.error(err.detail || 'No pude generar tu diploma');
                                    return;
                                  }
                                  const blob = await resp.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `Diploma_${(selectedPrueba.titulo || 'autocapacitacion').replace(/[^\w-]/g, '_')}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  window.URL.revokeObjectURL(url);
                                  toast.success('Diploma descargado');
                                } catch (e) {
                                  toast.error('Error al descargar el diploma');
                                }
                              }}
                              data-testid="descargar-diploma-btn"
                            >
                              <i className="fas fa-download mr-2" />
                              Descargar diploma
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Respuestas pregunta por pregunta */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-gray-800 flex items-center gap-2">
                          <i className="fas fa-clipboard-check text-indigo-600" />
                          Tus respuestas calificadas
                        </h4>
                        {ordenarPreguntasParaMostrar(selectedPrueba.preguntas || []).map((pregunta, idx) => {
                          const respEst = (misRespuestasDetalle.respuestas || []).find(r => r.pregunta_id === pregunta.id);
                          if (!respEst) return null;
                          const puntos = respEst.puntos_obtenidos;
                          const puntosMax = pregunta.puntos || 0;
                          const acerto = puntos != null && puntos >= puntosMax;
                          const parcial = puntos != null && puntos > 0 && puntos < puntosMax;
                          const fallo = puntos === 0;

                          // Determinar qué respuesta dio el estudiante
                          const respuestaEstudiante = respEst.respuesta_texto || respEst.respuesta_seleccionada || '';

                          // Caso especial: escala_1_10 NO se califica pero SÍ se muestra
                          // (el alumno puede ver qué valoración puso retroactivamente).
                          if (pregunta.tipo === 'escala_1_10') {
                            const valor = respEst.respuesta_seleccionada;
                            return (
                              <div
                                key={pregunta.id}
                                className="rounded-xl p-4 border-2 bg-indigo-50 border-indigo-200"
                                data-testid={`pregunta-detalle-${idx}`}
                              >
                                <p className="text-xs text-gray-500 font-semibold">Pregunta {idx + 1} · Valoración (no calificable)</p>
                                <p className="font-semibold text-gray-800 mt-1 whitespace-pre-wrap mb-3">{pregunta.enunciado}</p>
                                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                                  {valor ? (
                                    <EscalaRating
                                      value={valor}
                                      etiquetaIzq={pregunta.etiqueta_izquierda}
                                      etiquetaDer={pregunta.etiqueta_derecha}
                                      readOnly
                                    />
                                  ) : (
                                    <p className="text-sm italic text-gray-400 text-center py-2">No respondiste esta valoración (opcional)</p>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          // Saltarse preguntas informativas / material de apoyo
                          if (esNoCalificable(pregunta.tipo)) return null;

                          return (
                            <div
                              key={pregunta.id}
                              className={`rounded-xl p-4 border-2 ${
                                acerto ? 'bg-green-50 border-green-200' :
                                parcial ? 'bg-amber-50 border-amber-200' :
                                fallo ? 'bg-red-50 border-red-200' :
                                'bg-gray-50 border-gray-200'
                              }`}
                              data-testid={`pregunta-detalle-${idx}`}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1">
                                  <p className="text-xs text-gray-500 font-semibold">Pregunta {idx + 1}</p>
                                  <p className="font-semibold text-gray-800 mt-1 whitespace-pre-wrap">{pregunta.enunciado}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className={`text-xl font-bold ${
                                    acerto ? 'text-green-600' : parcial ? 'text-amber-600' : fallo ? 'text-red-600' : 'text-gray-500'
                                  }`}>
                                    {puntos != null ? puntos : '-'}/{puntosMax}
                                  </p>
                                  {acerto && <span className="text-xs text-green-700 font-semibold"><i className="fas fa-check mr-1" />Correcto</span>}
                                  {parcial && <span className="text-xs text-amber-700 font-semibold"><i className="fas fa-star-half-alt mr-1" />Parcial</span>}
                                  {fallo && <span className="text-xs text-red-700 font-semibold"><i className="fas fa-times mr-1" />Incorrecto</span>}
                                </div>
                              </div>

                              {/* Tu respuesta */}
                              <div className="bg-white rounded-lg p-3 border border-gray-200 mb-2">
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Tu respuesta</p>
                                {(() => {
                                  const opciones = pregunta.opciones || [];
                                  const textoOpcion = (val) => {
                                    if (!val) return '';
                                    const opt = opciones.find(o => o.letra === val || o === val);
                                    if (opt && typeof opt === 'object' && opt.texto) return opt.texto;
                                    if (pregunta.tipo === 'verdadero_falso') {
                                      const v = String(val).toLowerCase();
                                      if (v === 'v' || v === 'verdadero' || v === 'true') return 'Verdadero';
                                      if (v === 'f' || v === 'falso' || v === 'false') return 'Falso';
                                    }
                                    return '';
                                  };
                                  if (pregunta.tipo === 'desarrollo') {
                                    return (
                                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                        {respuestaEstudiante || <span className="italic text-gray-400">Sin respuesta</span>}
                                      </p>
                                    );
                                  }
                                  const sel = respEst.respuesta_seleccionada;
                                  const txt = textoOpcion(sel);
                                  if (!sel) return <p className="text-sm italic text-gray-400">Sin respuesta</p>;
                                  return (
                                    <p className="text-sm text-gray-800">
                                      <span className="font-bold">{sel}){' '}</span>
                                      {txt || <i className="text-gray-400">(opción no encontrada)</i>}
                                    </p>
                                  );
                                })()}
                              </div>

                              {/* Respuesta correcta (solo si el estudiante falló o parcial y la pregunta tiene respuesta correcta) */}
                              {pregunta.respuesta_correcta != null && pregunta.respuesta_correcta !== '' && !acerto && (
                                <div className="bg-white rounded-lg p-3 border border-green-200 mb-2">
                                  <p className="text-xs text-green-700 uppercase tracking-wide font-semibold mb-1">
                                    <i className="fas fa-lightbulb mr-1" />Respuesta correcta
                                  </p>
                                  {(() => {
                                    const opciones = pregunta.opciones || [];
                                    const textoOpcion = (val) => {
                                      if (!val) return '';
                                      const opt = opciones.find(o => o.letra === val || o === val);
                                      if (opt && typeof opt === 'object' && opt.texto) return opt.texto;
                                      if (pregunta.tipo === 'verdadero_falso') {
                                        const v = String(val).toLowerCase();
                                        if (v === 'v' || v === 'verdadero' || v === 'true') return 'Verdadero';
                                        if (v === 'f' || v === 'falso' || v === 'false') return 'Falso';
                                      }
                                      return '';
                                    };
                                    const correcta = pregunta.respuesta_correcta;
                                    const txt = textoOpcion(correcta);
                                    if (pregunta.tipo === 'desarrollo') {
                                      return <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(correcta)}</p>;
                                    }
                                    return (
                                      <p className="text-sm text-gray-800">
                                        <span className="font-bold">{correcta}){' '}</span>
                                        {txt || <i className="text-gray-400">(opción no encontrada)</i>}
                                      </p>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* Feedback automático (si hay) */}
                              {!acerto && pregunta.feedback_incorrecto && (
                                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 mb-2">
                                  <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold mb-1">
                                    <i className="fas fa-info-circle mr-1" />Feedback
                                  </p>
                                  <p className="text-sm text-gray-800">{pregunta.feedback_incorrecto}</p>
                                </div>
                              )}

                              {/* Comentario del profesor */}
                              {respEst.comentario_profesor && (
                                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                                  <p className="text-xs text-indigo-700 uppercase tracking-wide font-semibold mb-1">
                                    <i className="fas fa-comment-dots mr-1" />Comentario del profesor
                                  </p>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{respEst.comentario_profesor}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                      <i className="fas fa-info-circle mr-2" />
                      Todavía no respondiste esta prueba o no hay respuestas disponibles.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Dialog Editar Prueba */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-blue-50 to-cyan-50 border-4 border-blue-200 shadow-2xl">
          <DialogHeader className="border-b-4 border-blue-200 pb-4 mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 -m-6 mb-6 p-6 rounded-t-lg">
            <DialogTitle className="text-3xl font-black text-white flex items-center gap-3 drop-shadow-lg">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <i className="fas fa-edit text-3xl text-blue-600" />
              </div>
              Editar Prueba/Examen
            </DialogTitle>
            <p className="text-blue-100 mt-2 text-sm">Modifica los datos de la evaluación</p>
          </DialogHeader>

          <form onSubmit={handleActualizarPrueba} className="space-y-6">
            {/* Mismo formulario que crear */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Clase *</Label>
              <Select
                value={newPrueba.clase_id}
                onValueChange={(value) => setNewPrueba({...newPrueba, clase_id: value})}
              >
                <SelectTrigger className="border-2 border-indigo-200">
                  <SelectValue placeholder="Selecciona una clase" />
                </SelectTrigger>
                <SelectContent>
                  {clases.map(clase => (
                    <SelectItem key={clase.id} value={clase.id}>
                      {clase.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Título de la Prueba/Examen *</Label>
              <Input
                value={newPrueba.titulo}
                onChange={(e) => setNewPrueba({...newPrueba, titulo: e.target.value})}
                placeholder="Ej: Examen Parcial - Unidad 1"
                className="border-2 border-indigo-200"
              />
            </div>

            <div>
              <Label>Descripción / Instrucciones</Label>
              <Textarea
                value={newPrueba.descripcion}
                onChange={(e) => setNewPrueba({...newPrueba, descripcion: e.target.value})}
                placeholder="Instrucciones para el estudiante..."
                rows={3}
                className="border-2 border-indigo-200"
              />
            </div>

            <div>
              <Label>Tipo de Evaluación *</Label>
              <Select
                value={newPrueba.tipo}
                onValueChange={(value) => setNewPrueba({...newPrueba, tipo: value})}
              >
                <SelectTrigger className="border-2 border-indigo-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quiz">📝 Quiz/Prueba Rápida</SelectItem>
                  <SelectItem value="examen_parcial">📋 Examen Parcial</SelectItem>
                  <SelectItem value="examen_final">🎯 Examen Final</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50 py-3">
                <CardTitle className="text-base">Modalidad de Evaluación</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={newPrueba.modalidad === 'preguntas_sistema'}
                      onChange={() => setNewPrueba({...newPrueba, modalidad: 'preguntas_sistema'})}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold">📝 Preguntas en el Sistema</div>
                      <div className="text-xs text-gray-600">
                        Crea las preguntas aquí (desarrollo, múltiple opción, V/F)
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={newPrueba.modalidad === 'link_externo'}
                      onChange={() => setNewPrueba({...newPrueba, modalidad: 'link_externo'})}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold">🔗 Formulario Externo</div>
                      <div className="text-xs text-gray-600">
                        Google Classroom, Forms u otra plataforma
                      </div>
                    </div>
                  </label>

                  {newPrueba.modalidad === 'link_externo' && (
                    <Input
                      value={newPrueba.link_externo}
                      onChange={(e) => setNewPrueba({...newPrueba, link_externo: e.target.value})}
                      placeholder="https://classroom.google.com/..."
                      className="mt-2"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Zona Horaria */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <i className="fas fa-calendar-check text-green-600" />
                  Fecha Disponible *
                </Label>
                <Input
                  type="date"
                  value={newPrueba.fecha_disponible}
                  onChange={(e) => setNewPrueba({...newPrueba, fecha_disponible: e.target.value})}
                  className="border-2 border-indigo-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La prueba estará disponible desde las 00:00 de este día
                </p>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <i className="fas fa-calendar-times text-red-600" />
                  Fecha Límite *
                </Label>
                <Input
                  type="date"
                  value={newPrueba.fecha_limite}
                  onChange={(e) => setNewPrueba({...newPrueba, fecha_limite: e.target.value})}
                  className="border-2 border-indigo-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La prueba se cierra a las 23:59 de este día
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Puntos Totales *</Label>
                <Input
                  type="number"
                  value={newPrueba.puntos_totales}
                  onChange={(e) => setNewPrueba({...newPrueba, puntos_totales: parseInt(e.target.value)})}
                  className="border-2 border-indigo-200"
                />
              </div>
              <div>
                <Label>Peso en Nota (%)</Label>
                <Input
                  type="number"
                  value={newPrueba.peso_nota_final}
                  onChange={(e) => setNewPrueba({...newPrueba, peso_nota_final: parseInt(e.target.value)})}
                  className="border-2 border-indigo-200"
                />
              </div>
            </div>

            <div>
              <Label>Temario / Material de Estudio</Label>
              <Textarea
                value={newPrueba.temario}
                onChange={(e) => setNewPrueba({...newPrueba, temario: e.target.value})}
                placeholder="Capítulos 1-3, páginas 15-50..."
                rows={2}
                className="border-2 border-indigo-200"
              />
            </div>

            {/* Configuración avanzada — mismos checkboxes que el modal de Crear */}
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <i className="fas fa-cog text-amber-600" />
                  Configuración Avanzada
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="edit-check-calificar-cero">
                  <input
                    type="checkbox"
                    checked={!!newPrueba.calificar_automatico_cero}
                    onChange={(e) => setNewPrueba({...newPrueba, calificar_automatico_cero: e.target.checked})}
                    className="mt-1 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-amber-900">Calificar automáticamente con 0 si no entrega antes del cierre</span>
                    <p className="text-xs text-amber-700 mt-1">Si está marcado, los estudiantes que no entreguen recibirán 0 puntos al pasar la fecha límite.</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-amber-200" data-testid="edit-check-es-review">
                  <input
                    type="checkbox"
                    checked={!!newPrueba.es_review_prueba}
                    onChange={(e) => setNewPrueba({...newPrueba, es_review_prueba: e.target.checked})}
                    className="mt-1 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-purple-900">Es una prueba tipo Review/Encuesta (no se califica)</span>
                    <p className="text-xs text-purple-700 mt-1">Marcalo si la prueba es solo para feedback (escala 1–10, opinión, etc.). <strong>No aparecerá en Calificaciones ni afectará el promedio</strong>.</p>
                  </div>
                </label>

                {(() => {
                  const claseSel = clases.find(c => c.id === newPrueba.clase_id);
                  const esAutoCap = claseSel && (claseSel.categoria === 'autocapacitacion' || claseSel.categoria === 'autocapacitacion_profesores' || claseSel.es_autocapacitacion === true);
                  if (!esAutoCap) return null;
                  return (
                    <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-amber-200" data-testid="edit-check-es-prueba-final-cap">
                      <input
                        type="checkbox"
                        checked={!!newPrueba.es_prueba_final_capacitacion}
                        onChange={(e) => setNewPrueba({...newPrueba, es_prueba_final_capacitacion: e.target.checked})}
                        className="mt-1 w-5 h-5"
                      />
                      <div>
                        <span className="text-sm font-medium text-emerald-800">
                          <i className="fas fa-flag-checkered text-emerald-600 mr-1" />
                          Esta prueba finaliza la auto-capacitación
                        </span>
                        <p className="text-xs text-emerald-700 mt-1">Marcala solo en la <strong>última prueba</strong> del curso. Cuando un profesor no calificado la entregue, su cuenta se <strong>habilitará automáticamente</strong>.</p>
                      </div>
                    </label>
                  );
                })()}
              </CardContent>
            </Card>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingPrueba(null);
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
              >
                <i className="fas fa-save mr-2" />
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestión de Preguntas */}
      <Dialog
        open={isPreguntasDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsPreguntasDialogOpen(true);
            return;
          }
          // Al cerrar (X o click fuera): si la prueba requiere 100 pts y aún
          // no se llega, mostramos advertencia pero PERMITIMOS salir igual.
          // La validación dura corre solo cuando se intenta guardar/publicar.
          const p = selectedPrueba;
          if (p && p.requiere_100_puntos === true && p.es_review_prueba !== true) {
            const suma = (p.preguntas || []).reduce((acc, q) => acc + (Number(q.puntos) || 0), 0);
            if (suma !== 100) {
              const diff = 100 - suma;
              toast.warning(
                `⚠️ La prueba aún no suma 100 pts (actual: ${suma}). ${
                  diff > 0 ? `Faltan ${diff} pts.` : `Sobran ${Math.abs(diff)} pts.`
                } Podés salir y volver más tarde.`,
                { duration: 6000 }
              );
            }
          }
          setIsPreguntasDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-purple-50 to-pink-50 border-4 border-purple-200 shadow-2xl">
          <DialogHeader className="border-b-4 border-purple-200 pb-4 mb-4 bg-gradient-to-r from-purple-600 to-pink-600 -m-6 mb-6 p-6 rounded-t-lg">
            <DialogTitle className="text-3xl font-black text-white flex items-center gap-3 drop-shadow-lg">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <i className="fas fa-list-ol text-3xl text-purple-600" />
              </div>
              Gestión de Preguntas
            </DialogTitle>
            <p className="text-purple-100 mt-2 text-sm">Agrega, edita o elimina preguntas de esta evaluación</p>
          </DialogHeader>

          {selectedPrueba && (() => {
            // Suma de puntos calculada en vivo de las preguntas actuales.
            const sumaPuntos = (selectedPrueba.preguntas || []).reduce(
              (acc, p) => acc + (Number(p.puntos) || 0), 0
            );
            // La validación de 100 pts SOLO aplica si la prueba es nueva (flag requiere_100_puntos=true)
            // y NO es de tipo review/encuesta.
            const requiereValidacion = selectedPrueba.requiere_100_puntos === true && selectedPrueba.es_review_prueba !== true;
            const cumpleSuma = !requiereValidacion || sumaPuntos === 100;
            const sumaColor = !requiereValidacion
              ? 'bg-white text-gray-700'
              : sumaPuntos === 100
                ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-400'
                : sumaPuntos < 100
                  ? 'bg-amber-100 text-amber-800 border-2 border-amber-400'
                  : 'bg-red-100 text-red-800 border-2 border-red-400';
            const intentarCerrarPreguntasDialog = () => {
              // Mostrar advertencia pero permitir salir siempre (el profesor
              // puede quedarse sin tiempo o sin créditos de IA y necesita
              // poder volver más tarde sin quedarse trabado).
              if (requiereValidacion && sumaPuntos !== 100) {
                const diff = 100 - sumaPuntos;
                toast.warning(
                  `⚠️ La prueba aún no suma 100 pts (actual: ${sumaPuntos}). ${
                    diff > 0 ? `Faltan ${diff} pts.` : `Sobran ${Math.abs(diff)} pts.`
                  } Podés salir y volver más tarde.`,
                  { duration: 6000 }
                );
              }
              setIsPreguntasDialogOpen(false);
            };
            return (
            <div className="space-y-6">
              {/* Info de la prueba */}
              <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100 p-6 rounded-2xl border-4 border-purple-300 shadow-xl">
                <h3 className="font-black text-2xl text-purple-900">{selectedPrueba.titulo}</h3>
                <p className="text-sm text-purple-700 font-semibold mt-1">📚 {selectedPrueba.clase_nombre}</p>
                <div className="flex gap-3 mt-4 text-base font-bold flex-wrap">
                  <span className="bg-white px-4 py-2 rounded-xl shadow-md">🎯 {selectedPrueba.puntos_totales} pts totales</span>
                  <span className="bg-white px-4 py-2 rounded-xl shadow-md">📝 {selectedPrueba.preguntas?.length || 0} preguntas</span>
                  {requiereValidacion && (
                    <span
                      className={`px-4 py-2 rounded-xl shadow-md ${sumaColor}`}
                      data-testid="badge-suma-puntos"
                    >
                      {sumaPuntos === 100 ? '✅' : sumaPuntos < 100 ? '⚠️' : '❌'} Suma de preguntas: {sumaPuntos}/100
                    </span>
                  )}
                </div>
                {requiereValidacion && sumaPuntos !== 100 && (
                  <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded text-sm text-amber-900" data-testid="alerta-suma-puntos">
                    <strong>⚠️ Atención:</strong> La suma de puntos de las preguntas debe ser exactamente <strong>100</strong>. {sumaPuntos < 100 ? `Faltan ${100 - sumaPuntos} puntos.` : `Hay ${sumaPuntos - 100} puntos de más.`} Ajustá los puntos de las preguntas antes de cerrar.
                  </div>
                )}
              </div>

              {/* Botón Agregar Pregunta */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Button
                  className="bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl h-24 text-base font-bold border-4 border-green-300"
                  onClick={() => {
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('desarrollo');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 10, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-pen-fancy text-3xl" />
                    <span>Pregunta de Desarrollo</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-xl h-24 text-base font-bold border-4 border-blue-300"
                  onClick={() => {
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('multiple');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 10, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-list-ul text-3xl" />
                    <span>Opción Múltiple</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 shadow-xl h-24 text-base font-bold border-4 border-purple-300"
                  onClick={() => {
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('verdadero_falso');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 10, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-check-double text-3xl" />
                    <span>Verdadero/Falso</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-orange-500 to-yellow-600 hover:from-orange-600 hover:to-yellow-700 shadow-xl h-24 text-base font-bold border-4 border-orange-300"
                  onClick={() => {
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('texto_informativo');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 10, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-heading text-3xl" />
                    <span>Título/Subtítulo</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 shadow-xl h-24 text-base font-bold border-4 border-teal-300 relative"
                  onClick={() => {
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('material_apoyo');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 0, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                  data-testid="btn-material-apoyo"
                >
                  <span className="absolute top-1 right-2 text-[10px] font-bold bg-yellow-300 text-emerald-900 px-1.5 py-0.5 rounded">NUEVO</span>
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-book-reader text-3xl" />
                    <span>Material de Apoyo</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-xl h-24 text-base font-bold border-4 border-indigo-300 relative"
                  onClick={() => {
                    // Solo 1 pregunta de escala por prueba (como Material de Apoyo)
                    const yaTieneEscala = (selectedPrueba?.preguntas || []).some(p => p.tipo === 'escala_1_10');
                    if (yaTieneEscala) {
                      toast.error('Solo podés tener UNA pregunta de Escala 1-10 por prueba');
                      return;
                    }
                    setEditingPreguntaId(null);
                    setTipoPreguntaNueva('escala_1_10');
                    setNuevaPregunta({ enunciado: '', tipo_respuesta: 'texto_largo', puntos: 0, criterios: '', video_youtube_url: '', imagen_url: '', pdf_url: '', opciones: [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }, { letra: 'C', texto: '' }, { letra: 'D', texto: '' }], respuesta_correcta: '', feedback_correcto: '', feedback_incorrecto: '', etiqueta_izquierda: '', etiqueta_derecha: '' });
                    setIsAddPreguntaDialogOpen(true);
                  }}
                  data-testid="btn-escala-1-10"
                >
                  <span className="absolute top-1 right-2 text-[10px] font-bold bg-yellow-300 text-indigo-900 px-1.5 py-0.5 rounded">NUEVO</span>
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-chart-bar text-3xl" />
                    <span>Escala 1-10</span>
                  </div>
                </Button>
                <Button
                  className="bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 hover:from-indigo-600 hover:via-purple-700 hover:to-pink-700 shadow-xl h-24 text-base font-bold border-4 border-purple-300 relative overflow-hidden"
                  onClick={() => {
                    if (!selectedPrueba?.clase_id) {
                      toast.error('La prueba no tiene clase asociada');
                      return;
                    }
                    setIsGenesisGenOpen(true);
                  }}
                  data-testid="btn-genesis-generar"
                >
                  <span className="absolute top-1 right-2 text-[10px] font-bold bg-yellow-300 text-purple-900 px-1.5 py-0.5 rounded">NUEVO</span>
                  <div className="flex flex-col items-center gap-2">
                    <i className="fas fa-robot text-3xl" />
                    <span>Generar con IA</span>
                  </div>
                </Button>
              </div>

              {/* Lista de Preguntas */}
              {selectedPrueba.preguntas && selectedPrueba.preguntas.length > 0 ? (
                <div className="space-y-4">
                  {ordenarPreguntasParaMostrar(selectedPrueba.preguntas).map((pregunta, index) => (
                    <Card key={pregunta.id} className="border-4 border-indigo-200 shadow-lg hover:shadow-2xl transition-all duration-300">
                      <CardHeader className="bg-gradient-to-r from-indigo-100 to-purple-100 py-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg font-bold flex items-center gap-3">
                            <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-xl shadow-lg">
                              {index + 1}
                            </div>
                            <div>
                              <div className="text-indigo-900">
                                {pregunta.tipo === 'desarrollo' ? '📝 Desarrollo' : 
                                 pregunta.tipo === 'multiple' ? '✓ Opción Múltiple' : 
                                 pregunta.tipo === 'verdadero_falso' ? '✓/✗ V/F' :
                                 pregunta.tipo === 'texto_informativo' ? '📑 Título/Subtítulo' :
                                 pregunta.tipo === 'material_apoyo' ? '📚 Material de Apoyo' : 'Pregunta'}
                              </div>
                              <div className="text-sm text-indigo-600 font-semibold">
                                {esNoCalificable(pregunta.tipo) ? 'ℹ️ Sin puntaje' : `🎯 ${pregunta.puntos} puntos`}
                              </div>
                            </div>
                          </CardTitle>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg"
                              onClick={() => handleEditarPregunta(pregunta)}
                              data-testid={`edit-pregunta-${index}`}
                            >
                              <i className="fas fa-edit mr-2" />
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              className="bg-red-500 hover:bg-red-600 text-white shadow-lg"
                              onClick={() => handleEliminarPregunta(pregunta.id)}
                              data-testid={`delete-pregunta-${index}`}
                            >
                              <i className="fas fa-trash mr-2" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-6 bg-white">
                        <p className="text-base text-gray-800 leading-relaxed font-medium">{pregunta.enunciado}</p>
                        {pregunta.criterios && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                            <div className="text-sm font-bold text-blue-900 mb-2">📋 Criterios de Evaluación:</div>
                            <div className="text-sm text-blue-800 whitespace-pre-line">{pregunta.criterios}</div>
                          </div>
                        )}
                        {pregunta.opciones && (
                          <div className="mt-4 space-y-2">
                            <div className="text-sm font-bold text-indigo-900 mb-2">Opciones:</div>
                            {pregunta.opciones.map((opcion) => (
                              <div key={opcion.letra} className={`p-3 rounded-lg border-2 ${opcion.letra === pregunta.respuesta_correcta ? 'bg-green-100 border-green-400' : 'bg-gray-50 border-gray-200'}`}>
                                <span className="font-bold">{opcion.letra})</span> {opcion.texto}
                                {opcion.letra === pregunta.respuesta_correcta && <span className="ml-2 text-green-600 font-bold">✓ Correcta</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-4 border-dashed border-purple-300 shadow-inner">
                  <div className="text-7xl mb-6 animate-bounce">📝</div>
                  <h3 className="text-2xl font-black text-purple-900 mb-3">
                    ¡No hay preguntas creadas aún!
                  </h3>
                  <p className="text-base text-purple-700 mb-6 font-semibold">
                    Usa los botones de arriba para agregar tu primera pregunta
                  </p>
                  <div className="bg-white rounded-xl p-4 max-w-md mx-auto border-2 border-purple-200 shadow-lg">
                    <p className="text-sm text-gray-600">
                      💡 <strong>Tip:</strong> También puedes usar la modalidad "Link Externo" para usar Google Classroom
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={intentarCerrarPreguntasDialog}
                  className={cumpleSuma ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 hover:bg-gray-500 cursor-not-allowed"}
                  data-testid="btn-cerrar-preguntas-dialog"
                  title={cumpleSuma ? "Cerrar" : `La suma de puntos debe ser 100 (actual: ${sumaPuntos})`}
                >
                  Cerrar
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>


      {/* Dialog Agregar Pregunta */}
      <Dialog open={isAddPreguntaDialogOpen} onOpenChange={setIsAddPreguntaDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-green-50 to-emerald-50 border-4 border-green-200 shadow-2xl">
          <DialogHeader className={`border-b-4 pb-4 mb-4 -m-6 mb-6 p-6 rounded-t-lg ${
            tipoPreguntaNueva === 'desarrollo' ? 'bg-gradient-to-r from-green-600 to-emerald-600 border-green-200' :
            tipoPreguntaNueva === 'multiple' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 border-blue-200' :
            tipoPreguntaNueva === 'texto_informativo' ? 'bg-gradient-to-r from-orange-600 to-yellow-600 border-orange-200' :
            tipoPreguntaNueva === 'material_apoyo' ? 'bg-gradient-to-r from-teal-600 to-emerald-600 border-teal-200' :
            'bg-gradient-to-r from-purple-600 to-pink-600 border-purple-200'
          }`}>
            <DialogTitle className="text-3xl font-black text-white flex items-center gap-3 drop-shadow-lg">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                {tipoPreguntaNueva === 'desarrollo' && <i className="fas fa-pen-fancy text-4xl text-green-600" />}
                {tipoPreguntaNueva === 'multiple' && <i className="fas fa-list-ul text-4xl text-blue-600" />}
                {tipoPreguntaNueva === 'verdadero_falso' && <i className="fas fa-check-double text-4xl text-purple-600" />}
                {tipoPreguntaNueva === 'texto_informativo' && <i className="fas fa-heading text-4xl text-orange-600" />}
                {tipoPreguntaNueva === 'material_apoyo' && <i className="fas fa-book-reader text-4xl text-teal-600" />}
                {tipoPreguntaNueva === 'escala_1_10' && <i className="fas fa-chart-bar text-4xl text-indigo-600" />}
              </div>
              <div>
                {editingPreguntaId ? (
                  <>
                    {tipoPreguntaNueva === 'desarrollo' && 'Editar Pregunta de Desarrollo'}
                    {tipoPreguntaNueva === 'multiple' && 'Editar Pregunta Opción Múltiple'}
                    {tipoPreguntaNueva === 'verdadero_falso' && 'Editar Pregunta Verdadero/Falso'}
                    {tipoPreguntaNueva === 'texto_informativo' && 'Editar Título/Subtítulo'}
                    {tipoPreguntaNueva === 'material_apoyo' && 'Editar Material de Apoyo'}
                    {tipoPreguntaNueva === 'escala_1_10' && 'Editar Escala 1-10'}
                  </>
                ) : (
                  <>
                    {tipoPreguntaNueva === 'desarrollo' && 'Nueva Pregunta de Desarrollo'}
                    {tipoPreguntaNueva === 'multiple' && 'Nueva Pregunta Opción Múltiple'}
                    {tipoPreguntaNueva === 'verdadero_falso' && 'Nueva Pregunta Verdadero/Falso'}
                    {tipoPreguntaNueva === 'texto_informativo' && 'Nuevo Título/Subtítulo'}
                    {tipoPreguntaNueva === 'material_apoyo' && 'Nuevo Material de Apoyo'}
                    {tipoPreguntaNueva === 'escala_1_10' && 'Nueva Escala 1-10'}
                  </>
                )}
              </div>
            </DialogTitle>
            <p className="text-white/90 mt-2 text-sm ml-20">
              {tipoPreguntaNueva === 'desarrollo' && 'Pregunta abierta donde el estudiante desarrolla su respuesta'}
              {tipoPreguntaNueva === 'multiple' && 'Pregunta con 4 opciones, solo una es correcta (auto-calificable)'}
              {tipoPreguntaNueva === 'verdadero_falso' && 'Pregunta de Verdadero o Falso (auto-calificable)'}
              {tipoPreguntaNueva === 'texto_informativo' && 'Agrega un título, subtítulo o texto informativo sin pregunta'}
              {tipoPreguntaNueva === 'material_apoyo' && 'Inserta un video de YouTube o PDF que el alumno debe revisar antes de continuar (no calificable)'}
              {tipoPreguntaNueva === 'escala_1_10' && 'Pregunta de valoración 1 a 10 (estilo NPS). El alumno responde tocando un número. NO se califica.'}
            </p>
          </DialogHeader>

          <div className="space-y-6">
            {/* Enunciado o Texto Informativo */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-4 border-indigo-200">
              <Label className="text-xl font-black text-indigo-900 flex items-center gap-2 mb-3">
                <i className={`fas ${tipoPreguntaNueva === 'texto_informativo' ? 'fa-heading' : tipoPreguntaNueva === 'material_apoyo' ? 'fa-book-reader' : 'fa-question-circle'} text-indigo-600`} />
                {tipoPreguntaNueva === 'texto_informativo' ? 'Título o Texto Informativo *' : 
                 tipoPreguntaNueva === 'material_apoyo' ? 'Título / Descripción del Material *' : 
                 'Enunciado de la Pregunta *'}
              </Label>
              <Textarea
                value={nuevaPregunta.enunciado}
                onChange={(e) => setNuevaPregunta({...nuevaPregunta, enunciado: e.target.value})}
                placeholder={
                  tipoPreguntaNueva === 'texto_informativo' ? 'Escribe el título, subtítulo o texto informativo...' :
                  tipoPreguntaNueva === 'material_apoyo' ? 'Ej: "Antes de responder, mira este video sobre la Reforma Protestante..."' :
                  tipoPreguntaNueva === 'escala_1_10' ? 'Ej: "¿Cómo te pareció la clase?"' :
                  'Escribe aquí la pregunta completa...'
                }
                rows={esNoCalificable(tipoPreguntaNueva) ? 3 : 5}
                className="border-4 border-indigo-200 mt-2 text-base focus:border-indigo-500 rounded-xl"
              />
              {tipoPreguntaNueva === 'texto_informativo' && (
                <p className="text-xs text-indigo-600 mt-2">
                  💡 Este texto aparecerá como un separador visual entre preguntas
                </p>
              )}
              {tipoPreguntaNueva === 'material_apoyo' && (
                <p className="text-xs text-teal-700 mt-2">
                  💡 Esta descripción se mostrará junto al video/PDF para guiar al alumno
                </p>
              )}
            </div>

            {/* Material de Apoyo: Video YouTube + PDF */}
            {tipoPreguntaNueva === 'material_apoyo' && (
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-6 shadow-lg border-4 border-teal-300 space-y-4">
                <h3 className="text-xl font-black text-teal-900 flex items-center gap-2">
                  <i className="fas fa-photo-video text-teal-600" />
                  Recurso del Material de Apoyo *
                </h3>
                <p className="text-sm text-teal-700 -mt-2">
                  Agrega un video de YouTube y/o un documento (PDF, PowerPoint, Word, Excel, Drive). Debes completar al menos uno.
                </p>

                {/* Video YouTube */}
                <div>
                  <Label className="font-bold text-teal-900 flex items-center gap-2">
                    <i className="fab fa-youtube text-red-600" />
                    URL de Video de YouTube
                  </Label>
                  <Input
                    type="url"
                    value={nuevaPregunta.video_youtube_url}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, video_youtube_url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="border-2 border-teal-300 mt-1"
                    data-testid="input-material-youtube"
                  />
                  <p className="text-xs text-teal-700 mt-1">Pega aquí el link del video (se mostrará embebido)</p>
                </div>

                {/* Documento (PDF / Word / Excel / PowerPoint) */}
                <div>
                  <Label className="font-bold text-teal-900 flex items-center gap-2">
                    <i className="fas fa-file-alt text-teal-700" />
                    URL de Documento (opcional)
                  </Label>
                  <Input
                    type="url"
                    value={nuevaPregunta.pdf_url}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, pdf_url: e.target.value})}
                    placeholder="https://drive.google.com/file/d/.../view  o  https://...pptx / .pdf / .docx"
                    className="border-2 border-teal-300 mt-1"
                    data-testid="input-material-pdf"
                  />
                  <p className="text-xs text-teal-700 mt-1">
                    Acepta: <strong>PDF</strong>, <strong>PowerPoint</strong> (ppt/pptx/pps/ppsx),
                    <strong> Word</strong> (doc/docx), <strong>Excel</strong> (xls/xlsx) y links de
                    <strong> Google Drive</strong>. Se muestra embebido + botón de descarga.
                  </p>
                </div>
              </div>
            )}

            {/* Escala 1-10: etiquetas opcionales para los extremos + preview */}
            {tipoPreguntaNueva === 'escala_1_10' && (
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 shadow-lg border-4 border-indigo-300 space-y-4">
                <h3 className="text-xl font-black text-indigo-900 flex items-center gap-2">
                  <i className="fas fa-chart-bar text-indigo-600" />
                  Configuración de la Escala
                </h3>
                <p className="text-sm text-indigo-700 -mt-2">
                  Etiquetas opcionales para guiar al alumno. Si las dejás vacías, solo se muestran los números 1 a 10.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold text-indigo-900 flex items-center gap-2">
                      <span className="inline-block w-7 h-7 bg-red-400 text-white rounded-lg flex items-center justify-center font-bold text-sm">1</span>
                      Etiqueta extremo izquierdo
                    </Label>
                    <Input
                      type="text"
                      value={nuevaPregunta.etiqueta_izquierda || ''}
                      onChange={(e) => setNuevaPregunta({...nuevaPregunta, etiqueta_izquierda: e.target.value})}
                      placeholder='Ej: "No me enganchó"'
                      className="border-2 border-indigo-300 mt-1"
                      data-testid="input-escala-etiqueta-izq"
                    />
                  </div>
                  <div>
                    <Label className="font-bold text-indigo-900 flex items-center gap-2">
                      <span className="inline-block w-7 h-7 bg-emerald-500 text-white rounded-lg flex items-center justify-center font-bold text-sm">10</span>
                      Etiqueta extremo derecho
                    </Label>
                    <Input
                      type="text"
                      value={nuevaPregunta.etiqueta_derecha || ''}
                      onChange={(e) => setNuevaPregunta({...nuevaPregunta, etiqueta_derecha: e.target.value})}
                      placeholder='Ej: "Excelente"'
                      className="border-2 border-indigo-300 mt-1"
                      data-testid="input-escala-etiqueta-der"
                    />
                  </div>
                </div>

                <div className="border-t-2 border-indigo-200 pt-4">
                  <p className="text-sm font-bold text-indigo-900 mb-2">Vista previa de lo que verá el alumno:</p>
                  <div className="bg-white rounded-xl p-4 border-2 border-indigo-200">
                    <EscalaRating
                      value={null}
                      etiquetaIzq={nuevaPregunta.etiqueta_izquierda || ''}
                      etiquetaDer={nuevaPregunta.etiqueta_derecha || ''}
                      readOnly
                    />
                    <p className="text-xs text-gray-500 text-center mt-2 italic">
                      💡 Es <strong>opcional</strong> y <strong>no afecta la calificación</strong> de la prueba.
                    </p>
                  </div>
                </div>

                {/* Marcar como Review */}
                <div className={`mt-4 p-4 rounded-xl border-2 ${nuevaPregunta.es_review ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!nuevaPregunta.es_review}
                      onChange={(e) => setNuevaPregunta({ ...nuevaPregunta, es_review: e.target.checked })}
                      className="w-5 h-5 accent-purple-600 mt-0.5"
                      data-testid="pregunta-es-review"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 flex items-center gap-2">
                        <i className="fas fa-chart-column text-purple-600"></i>
                        Marcar como <strong className="text-purple-700">Review</strong> (ver totales por nota)
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Al marcar esto, profesores y administradores podrán ver desde el detalle de la prueba un <strong>histograma</strong> con cuántos estudiantes pusieron cada nota (10, 9, 8...) + el promedio. Útil para encuestas de fin de curso, NPS, opinión del alumno.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Puntos - Solo para preguntas reales, no para texto informativo / material apoyo */}
            {!esNoCalificable(tipoPreguntaNueva) && (
              <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl p-6 shadow-lg border-4 border-yellow-300">
                <Label className="text-xl font-black text-orange-900 flex items-center gap-2 mb-3">
                  <i className="fas fa-star text-yellow-600" />
                  Puntos Asignados *
                </Label>
                <Input
                  type="number"
                  value={nuevaPregunta.puntos}
                  onChange={(e) => setNuevaPregunta({...nuevaPregunta, puntos: parseInt(e.target.value)})}
                  className="border-4 border-yellow-300 mt-1 text-2xl font-bold text-center h-16 rounded-xl"
                  min="1"
                />
                <p className="text-xs text-orange-700 mt-2">🎯 Puntos que vale esta pregunta en el examen</p>
              </div>
            )}

            {/* Recursos Multimedia (NUEVO) - Solo para preguntas reales */}
            {!esNoCalificable(tipoPreguntaNueva) && (
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl p-6 shadow-lg border-4 border-pink-300">
              <h3 className="text-xl font-black text-purple-900 flex items-center gap-2 mb-4">
                <i className="fas fa-photo-video text-pink-600" />
                Recursos Multimedia (Opcional)
              </h3>
              <p className="text-sm text-purple-700 mb-4">Agrega videos, imágenes o texto informativo para enriquecer la pregunta</p>
              
              <div className="space-y-4">
                {/* Video YouTube */}
                <div>
                  <Label className="font-bold text-purple-900 flex items-center gap-2">
                    <i className="fab fa-youtube text-red-600" />
                    Video de YouTube
                  </Label>
                  <Input
                    type="url"
                    value={nuevaPregunta.video_youtube_url}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, video_youtube_url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="border-2 border-pink-300 mt-1"
                  />
                  <p className="text-xs text-purple-600 mt-1">El video se mostrará antes de la pregunta</p>
                </div>

                {/* Imagen */}
                <div>
                  <Label className="font-bold text-purple-900 flex items-center gap-2">
                    <i className="fas fa-image text-blue-600" />
                    URL de Imagen
                  </Label>
                  <Input
                    type="url"
                    value={nuevaPregunta.imagen_url}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, imagen_url: e.target.value})}
                    placeholder="https://ejemplo.com/imagen.jpg"
                    className="border-2 border-pink-300 mt-1"
                  />
                  <p className="text-xs text-purple-600 mt-1">La imagen se mostrará antes de la pregunta</p>
                </div>

              </div>
            </div>
            )}

            {/* Campos específicos por tipo */}
            {tipoPreguntaNueva === 'desarrollo' && (
              <>
                <div>
                  <Label>Tipo de Respuesta Esperada</Label>
                  <Select
                    value={nuevaPregunta.tipo_respuesta}
                    onValueChange={(value) => setNuevaPregunta({...nuevaPregunta, tipo_respuesta: value})}
                  >
                    <SelectTrigger className="border-2 border-indigo-200 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="texto_corto">Texto corto (hasta 500 caracteres)</SelectItem>
                      <SelectItem value="texto_largo">Texto largo (ensayo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Criterios de Evaluación (opcional)</Label>
                  <Textarea
                    value={nuevaPregunta.criterios}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, criterios: e.target.value})}
                    placeholder="Ej: • Definición clara (3 pts)&#10;• Ejemplos prácticos (4 pts)&#10;• Claridad (3 pts)"
                    rows={4}
                    className="border-2 border-indigo-200 mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Esto te ayudará al calificar</p>
                </div>
              </>
            )}

            {tipoPreguntaNueva === 'multiple' && (
              <>
                <div className="bg-white rounded-2xl p-6 shadow-lg border-4 border-blue-200 space-y-4">
                  <Label className="text-xl font-black text-blue-900 flex items-center gap-2">
                    <i className="fas fa-list-ul text-blue-600" />
                    Opciones de Respuesta
                  </Label>
                  {nuevaPregunta.opciones.map((opcion, index) => (
                    <div key={opcion.letra} className="flex gap-3 items-center">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center font-black text-white text-2xl shadow-lg">
                        {opcion.letra}
                      </div>
                      <Input
                        value={opcion.texto}
                        onChange={(e) => {
                          const nuevasOpciones = [...nuevaPregunta.opciones];
                          nuevasOpciones[index].texto = e.target.value;
                          setNuevaPregunta({...nuevaPregunta, opciones: nuevasOpciones});
                        }}
                        placeholder={`Escribe la opción ${opcion.letra}...`}
                        className="flex-1 border-4 border-blue-200 text-base h-14 rounded-xl"
                      />
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl p-6 shadow-lg border-4 border-green-300">
                  <Label className="text-xl font-black text-green-900 flex items-center gap-2 mb-3">
                    <i className="fas fa-check-circle text-green-600 text-2xl" />
                    Respuesta Correcta *
                  </Label>
                  <Select
                    value={nuevaPregunta.respuesta_correcta}
                    onValueChange={(value) => setNuevaPregunta({...nuevaPregunta, respuesta_correcta: value})}
                  >
                    <SelectTrigger className="border-4 border-green-300 mt-1 h-14 text-base rounded-xl bg-white">
                      <SelectValue placeholder="Selecciona la opción correcta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - {nuevaPregunta.opciones[0].texto || 'Opción A'}</SelectItem>
                      <SelectItem value="B">B - {nuevaPregunta.opciones[1].texto || 'Opción B'}</SelectItem>
                      <SelectItem value="C">C - {nuevaPregunta.opciones[2].texto || 'Opción C'}</SelectItem>
                      <SelectItem value="D">D - {nuevaPregunta.opciones[3].texto || 'Opción D'}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-green-700 mt-2">✓ Esta opción se marcará como correcta automáticamente</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Feedback si es Correcta</Label>
                    <Input
                      value={nuevaPregunta.feedback_correcto}
                      onChange={(e) => setNuevaPregunta({...nuevaPregunta, feedback_correcto: e.target.value})}
                      placeholder="¡Excelente!"
                      className="border-2 border-green-200 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Feedback si es Incorrecta</Label>
                    <Input
                      value={nuevaPregunta.feedback_incorrecto}
                      onChange={(e) => setNuevaPregunta({...nuevaPregunta, feedback_incorrecto: e.target.value})}
                      placeholder="Ver capítulo 1, página 15"
                      className="border-2 border-red-200 mt-1"
                    />
                  </div>
                </div>
              </>
            )}

            {tipoPreguntaNueva === 'verdadero_falso' && (
              <>
                <div>
                  <Label>Respuesta Correcta *</Label>
                  <Select
                    value={nuevaPregunta.respuesta_correcta}
                    onValueChange={(value) => setNuevaPregunta({...nuevaPregunta, respuesta_correcta: value})}
                  >
                    <SelectTrigger className="border-2 border-green-200 mt-1">
                      <SelectValue placeholder="Selecciona Verdadero o Falso" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Verdadero">✓ Verdadero</SelectItem>
                      <SelectItem value="Falso">✗ Falso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Explicación (mostrar después de responder)</Label>
                  <Textarea
                    value={nuevaPregunta.feedback_correcto}
                    onChange={(e) => setNuevaPregunta({...nuevaPregunta, feedback_correcto: e.target.value})}
                    placeholder="Explicación de por qué es verdadero o falso..."
                    rows={3}
                    className="border-2 border-indigo-200 mt-1"
                  />
                </div>
              </>
            )}

            {/* Botones */}
            <div className="flex gap-4 pt-6 border-t-4 border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddPreguntaDialogOpen(false);
                  setEditingPreguntaId(null);
                  setNuevaPregunta({
                    enunciado: '',
                    tipo_respuesta: 'texto_largo',
                    puntos: 10,
                    criterios: '',
                    video_youtube_url: '',
                    imagen_url: '',
                    pdf_url: '',
                    opciones: [
                      { letra: 'A', texto: '' },
                      { letra: 'B', texto: '' },
                      { letra: 'C', texto: '' },
                      { letra: 'D', texto: '' }
                    ],
                    respuesta_correcta: '',
                    feedback_correcto: '',
                    feedback_incorrecto: ''
                  });
                }}
                className="flex-1 h-16 text-lg font-bold border-4 border-gray-300 hover:bg-gray-100"
              >
                <i className="fas fa-times mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleAgregarPregunta}
                className="flex-1 h-16 text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-xl border-4 border-green-300"
                data-testid="save-pregunta-btn"
              >
                <i className={`fas ${editingPreguntaId ? 'fa-save' : 'fa-check-circle'} mr-2 text-2xl`} />
                {editingPreguntaId ? 'Guardar Cambios' : 'Agregar Pregunta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Tomar Prueba (Estudiante) */}
      <Dialog open={isTomandoPruebaDialogOpen} onOpenChange={handleCerrarPrueba}>
        <DialogContent className="w-screen h-screen max-w-full sm:w-[95vw] sm:max-w-6xl sm:h-auto sm:max-h-[90vh] overflow-y-auto overflow-x-hidden bg-gradient-to-br from-white via-green-50 to-blue-50 border-0 sm:border-4 border-green-300 shadow-2xl p-2 sm:p-6 rounded-none sm:rounded-lg">
          <DialogHeader className="border-b-2 sm:border-b-4 border-green-300 pb-2 sm:pb-4 mb-2 sm:mb-4 bg-gradient-to-r from-green-600 to-blue-600 -m-2 sm:-m-6 mb-2 sm:mb-6 p-3 sm:p-6 rounded-none sm:rounded-t-lg">
            <DialogTitle className="text-base sm:text-3xl font-black text-white flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 pr-8">
                <div className="w-9 h-9 sm:w-14 sm:h-14 bg-white rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shrink-0">
                  <i className="fas fa-pencil-alt text-base sm:text-3xl text-green-600" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm sm:text-3xl">{selectedPrueba?.titulo}</div>
                  <div className="text-[11px] sm:text-sm text-white/90 font-normal truncate">{selectedPrueba?.clase_nombre}</div>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedPrueba && (
            <div className="space-y-6">
              {/* Instrucciones */}
              {selectedPrueba.descripcion && (
                <div className="bg-blue-50 border-2 sm:border-4 border-blue-200 rounded-xl sm:rounded-2xl p-3 sm:p-6">
                  <h3 className="font-bold text-base sm:text-lg text-blue-900 mb-1 sm:mb-2">
                    <i className="fas fa-info-circle mr-2" />
                    Instrucciones
                  </h3>
                  <p className="text-sm sm:text-base text-blue-800 break-words whitespace-pre-wrap">{selectedPrueba.descripcion}</p>
                </div>
              )}

              {/* Navegación de preguntas (solo preguntas calificables) */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-2 sm:p-4 shadow-lg border-2 sm:border-4 border-indigo-200">
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  {selectedPrueba.preguntas?.map((pregunta, index) => {
                    if (esNoCalificable(pregunta.tipo)) return null;
                    // Numeración solo sobre preguntas reales
                    const numeroPregunta = selectedPrueba.preguntas
                      .slice(0, index)
                      .filter(p => !esNoCalificable(p.tipo)).length + 1;
                    return (
                      <button
                        key={pregunta.id}
                        className={`w-9 h-9 sm:w-12 sm:h-12 rounded-md sm:rounded-lg font-bold text-sm sm:text-base transition-all ${
                          respuestasEstudiante[index]?.respuesta_texto || respuestasEstudiante[index]?.respuesta_seleccionada
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                        onClick={() => document.getElementById(`pregunta-${index}`)?.scrollIntoView({ behavior: 'smooth' })}
                      >
                        {numeroPregunta}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] sm:text-xs text-gray-600 mt-1 sm:mt-2">
                  <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded mr-1"></span> Respondida
                  <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 bg-gray-200 rounded ml-2 sm:ml-3 mr-1"></span> Pendiente
                </p>
              </div>

              {/* Preguntas */}
              <div className="space-y-4 sm:space-y-6">
                {selectedPrueba.preguntas?.map((pregunta, index) => (
                  <div 
                    key={pregunta.id} 
                    id={`pregunta-${index}`}
                    className={
                      pregunta.tipo === 'texto_informativo' ? "bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 sm:border-4 border-orange-300 break-words" :
                      pregunta.tipo === 'material_apoyo' ? "bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 sm:border-4 border-teal-300 break-words" :
                      pregunta.tipo === 'escala_1_10' ? "bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 sm:border-4 border-indigo-300 break-words" :
                      "bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 sm:border-4 border-purple-200 break-words"
                    }
                  >
                    {pregunta.tipo === 'texto_informativo' ? (
                      // Título/Subtítulo - Sin numeración ni puntos
                      <div className="flex items-start gap-2 sm:gap-4">
                        <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-600 to-yellow-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                          <i className="fas fa-heading text-lg sm:text-3xl" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base sm:text-2xl text-orange-900 font-black leading-relaxed break-words">
                            {pregunta.enunciado}
                          </p>
                        </div>
                      </div>
                    ) : pregunta.tipo === 'material_apoyo' ? (
                      // Material de Apoyo - Video YouTube y/o PDF
                      <div data-testid={`material-apoyo-${index}`}>
                        <div className="flex items-start gap-2 sm:gap-4 mb-3 sm:mb-4">
                          <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-teal-600 to-emerald-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                            <i className="fas fa-book-reader text-lg sm:text-3xl" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="inline-block bg-teal-200 text-teal-900 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold mb-1 sm:mb-2">
                              📚 MATERIAL DE APOYO
                            </div>
                            {pregunta.enunciado && (
                              <p className="text-sm sm:text-lg text-teal-900 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                                {pregunta.enunciado}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Video YouTube embebido */}
                        {pregunta.video_youtube_url && (() => {
                          const url = pregunta.video_youtube_url;
                          // Convertir a embed url
                          let embedUrl = url;
                          const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
                          if (watchMatch) {
                            embedUrl = `https://www.youtube.com/embed/${watchMatch[1]}`;
                          }
                          return (
                            <div className="bg-white border-2 sm:border-4 border-teal-200 rounded-lg sm:rounded-xl p-2 sm:p-3 mb-2 sm:mb-3 shadow">
                              <div className="relative w-full aspect-video rounded-md sm:rounded-lg overflow-hidden">
                                <iframe
                                  width="100%"
                                  height="100%"
                                  src={embedUrl}
                                  title="Material de Apoyo - Video"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="absolute top-0 left-0 w-full h-full"
                                />
                              </div>
                            </div>
                          );
                        })()}

                        {/* Documento embebido (PDF / Office / Google Drive)
                            Detecta el tipo del archivo por URL y renderiza el
                            viewer apropiado: Drive Preview para drive.google.com,
                            Office Online Viewer para .ppt/.docx/.xlsx, e iframe
                            directo para PDF. SIEMPRE muestra botón de descarga. */}
                        {pregunta.pdf_url && (() => {
                          const cfg = getDocumentoEmbedConfig(pregunta.pdf_url);
                          if (!cfg) return null;
                          const labelTipo = cfg.type === 'office' ? 'Presentación / Documento Office' : cfg.type === 'drive' ? 'Documento (Google Drive)' : cfg.type === 'pdf' ? 'Documento PDF' : 'Documento';
                          const iconCls = cfg.type === 'office' ? 'fa-file-powerpoint text-orange-600' : cfg.type === 'drive' ? 'fa-cloud text-blue-600' : 'fa-file-pdf text-red-600';
                          const borderColor = cfg.type === 'office' ? 'border-orange-200' : cfg.type === 'drive' ? 'border-blue-200' : 'border-red-200';
                          return (
                            <div className={`bg-white border-2 sm:border-4 ${borderColor} rounded-lg sm:rounded-xl p-2 sm:p-3 shadow space-y-2`} data-testid={`material-apoyo-doc-${index}`}>
                              {/* Header con título + botones */}
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                  <i className={`fas ${iconCls} text-2xl sm:text-3xl shrink-0`} />
                                  <span className="text-sm sm:text-base font-bold text-gray-800 truncate">{labelTipo}</span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <a
                                    href={cfg.openUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition"
                                    title="Abrir en pestaña nueva"
                                  >
                                    <i className="fas fa-external-link-alt" />
                                    <span className="hidden sm:inline">Abrir</span>
                                  </a>
                                  <a
                                    href={cfg.downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition"
                                    title="Descargar archivo"
                                    data-testid={`material-doc-download-${index}`}
                                  >
                                    <i className="fas fa-download" />
                                    <span className="hidden sm:inline">Descargar</span>
                                  </a>
                                </div>
                              </div>
                              {/* Visor embebido */}
                              {cfg.embedUrl && (
                                <div className="relative w-full rounded-md overflow-hidden bg-gray-100" style={{ aspectRatio: '16/10', minHeight: '380px' }}>
                                  <iframe
                                    src={cfg.embedUrl}
                                    title={labelTipo}
                                    frameBorder="0"
                                    allow="autoplay"
                                    allowFullScreen
                                    className="absolute top-0 left-0 w-full h-full"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ) : pregunta.tipo === 'escala_1_10' ? (
                      // Escala 1-10 (feedback no calificable, opcional)
                      <div data-testid={`escala-1-10-${index}`}>
                        <div className="flex items-start gap-2 sm:gap-4 mb-3 sm:mb-4">
                          <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-600 to-blue-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                            <i className="fas fa-chart-bar text-lg sm:text-3xl" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="inline-block bg-indigo-200 text-indigo-900 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold mb-1 sm:mb-2">
                              📊 VALORACIÓN (OPCIONAL)
                            </div>
                            <p className="text-sm sm:text-lg text-indigo-900 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                              {pregunta.enunciado}
                            </p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border-2 border-indigo-200">
                          <EscalaRating
                            value={respuestasEstudiante[index]?.respuesta_seleccionada}
                            onChange={(n) => {
                              const nuevasRespuestas = [...respuestasEstudiante];
                              if (nuevasRespuestas[index]) {
                                nuevasRespuestas[index].respuesta_seleccionada = String(n);
                                setRespuestasEstudiante(nuevasRespuestas);
                              }
                            }}
                            etiquetaIzq={pregunta.etiqueta_izquierda}
                            etiquetaDer={pregunta.etiqueta_derecha}
                          />
                          <p className="text-xs text-gray-500 text-center mt-2 italic">
                            Esta pregunta NO afecta tu calificación. Es feedback para el profesor.
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Pregunta normal con numeración y puntos
                      <div className="flex items-start gap-2 sm:gap-4 mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center font-black text-lg sm:text-2xl shadow-lg flex-shrink-0">
                          {pregunta.orden}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <div className="font-bold text-purple-900 text-xs sm:text-base">
                              {pregunta.tipo === 'desarrollo' ? '📝 Desarrollo' : 
                               pregunta.tipo === 'multiple' ? '✓ Opción Múltiple' : 
                               '✓/✗ Verdadero/Falso'}
                            </div>
                            <div className="bg-yellow-100 px-2 py-1 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl font-bold text-yellow-800 border-2 border-yellow-300 text-xs sm:text-base">
                              🎯 {pregunta.puntos} pts
                            </div>
                          </div>
                          <p className="text-sm sm:text-lg text-gray-800 font-medium leading-relaxed break-words whitespace-pre-wrap">
                            {pregunta.enunciado}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Recursos Multimedia (Video, Imagen) — solo para preguntas calificables.
                        Material de Apoyo renderiza su propio video/PDF arriba. */}
                    {!esNoCalificable(pregunta.tipo) && (pregunta.video_youtube_url || pregunta.imagen_url) && (
                      <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
                        {/* Video de YouTube */}
                        {pregunta.video_youtube_url && (() => {
                          // Normalizar la URL a formato embed (soporta watch?v=, youtu.be, shorts, embed)
                          const rawUrl = pregunta.video_youtube_url;
                          const m = rawUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
                          const embedUrl = m ? `https://www.youtube.com/embed/${m[1]}` : rawUrl;
                          return (
                          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-4 border-red-300 rounded-xl p-4">
                            <h4 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                              <i className="fab fa-youtube text-red-600" />
                              Video para esta pregunta
                            </h4>
                            <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg">
                              <iframe
                                width="100%"
                                height="100%"
                                src={embedUrl}
                                title="Video YouTube"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="absolute top-0 left-0 w-full h-full"
                              />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 truncate">
                              <a href={rawUrl} target="_blank" rel="noopener noreferrer" className="hover:text-red-600 underline">
                                Abrir en YouTube
                              </a>
                            </p>
                          </div>
                          );
                        })()}

                        {/* Imagen */}
                        {pregunta.imagen_url && (
                          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-4 border-blue-300 rounded-xl p-4">
                            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                              <i className="fas fa-image text-blue-600" />
                              Imagen de referencia
                            </h4>
                            <img
                              src={pregunta.imagen_url}
                              alt="Imagen de la pregunta"
                              className="w-full max-w-2xl mx-auto rounded-lg shadow-lg border-2 border-blue-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML += '<p class="text-red-600 text-center">⚠️ No se pudo cargar la imagen</p>';
                              }}
                            />
                          </div>
                        )}

                      </div>
                    )}

                    {/* Campo de respuesta según tipo - Solo si NO es texto_informativo / material_apoyo */}
                    {!esNoCalificable(pregunta.tipo) && (
                    <div>
                    {pregunta.tipo === 'desarrollo' && (
                      <div className="mt-3 sm:mt-4">
                        <Label className="font-bold text-gray-700 mb-1 sm:mb-2 block text-sm sm:text-base">Tu Respuesta:</Label>
                        <Textarea
                          value={respuestasEstudiante[index]?.respuesta_texto || ''}
                          onChange={(e) => {
                            const nuevasRespuestas = [...respuestasEstudiante];
                            nuevasRespuestas[index].respuesta_texto = e.target.value;
                            setRespuestasEstudiante(nuevasRespuestas);
                          }}
                          rows={pregunta.tipo_respuesta === 'texto_largo' ? 8 : 4}
                          placeholder="Escribe tu respuesta aquí..."
                          className="border-2 sm:border-4 border-purple-200 focus:border-purple-500 rounded-lg sm:rounded-xl text-sm sm:text-base w-full"
                          onPaste={(e) => {
                            if (selectedPrueba.config?.prevenir_copy_paste) {
                              e.preventDefault();
                              toast.warning('⚠️ Copiar/pegar está deshabilitado');
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1 sm:mt-2">
                          📝 Respuesta registrada automáticamente al escribir
                        </p>
                      </div>
                    )}

                    {pregunta.tipo === 'multiple' && pregunta.opciones && (
                      <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                        {pregunta.opciones.map((opcion) => (
                          <label
                            key={opcion.letra}
                            className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-lg sm:rounded-xl border-2 sm:border-4 cursor-pointer transition-all ${
                              respuestasEstudiante[index]?.respuesta_seleccionada === opcion.letra
                                ? 'bg-purple-100 border-purple-500'
                                : 'bg-gray-50 border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`pregunta-${pregunta.id}`}
                              checked={respuestasEstudiante[index]?.respuesta_seleccionada === opcion.letra}
                              onChange={() => {
                                const nuevasRespuestas = [...respuestasEstudiante];
                                nuevasRespuestas[index].respuesta_seleccionada = opcion.letra;
                                setRespuestasEstudiante(nuevasRespuestas);
                              }}
                              className="w-5 h-5 sm:w-6 sm:h-6 shrink-0"
                            />
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 text-white rounded-md sm:rounded-lg flex items-center justify-center font-bold text-sm sm:text-base shrink-0">
                              {opcion.letra}
                            </div>
                            <span className="flex-1 text-gray-800 font-medium text-sm sm:text-base break-words min-w-0">{opcion.texto}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {pregunta.tipo === 'verdadero_falso' && (
                      <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-4">
                        <label
                          className={`flex items-center justify-center gap-2 sm:gap-3 p-3 sm:p-6 rounded-lg sm:rounded-xl border-2 sm:border-4 cursor-pointer transition-all ${
                            respuestasEstudiante[index]?.respuesta_seleccionada === 'Verdadero'
                              ? 'bg-green-100 border-green-500'
                              : 'bg-gray-50 border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`pregunta-${pregunta.id}`}
                            checked={respuestasEstudiante[index]?.respuesta_seleccionada === 'Verdadero'}
                            onChange={() => {
                              const nuevasRespuestas = [...respuestasEstudiante];
                              nuevasRespuestas[index].respuesta_seleccionada = 'Verdadero';
                              setRespuestasEstudiante(nuevasRespuestas);
                            }}
                            className="w-5 h-5 sm:w-6 sm:h-6 shrink-0"
                          />
                          <i className="fas fa-check-circle text-2xl sm:text-4xl text-green-600" />
                          <span className="text-base sm:text-xl font-bold text-green-800">Verdadero</span>
                        </label>
                        <label
                          className={`flex items-center justify-center gap-2 sm:gap-3 p-3 sm:p-6 rounded-lg sm:rounded-xl border-2 sm:border-4 cursor-pointer transition-all ${
                            respuestasEstudiante[index]?.respuesta_seleccionada === 'Falso'
                              ? 'bg-red-100 border-red-500'
                              : 'bg-gray-50 border-gray-200 hover:border-red-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`pregunta-${pregunta.id}`}
                            checked={respuestasEstudiante[index]?.respuesta_seleccionada === 'Falso'}
                            onChange={() => {
                              const nuevasRespuestas = [...respuestasEstudiante];
                              nuevasRespuestas[index].respuesta_seleccionada = 'Falso';
                              setRespuestasEstudiante(nuevasRespuestas);
                            }}
                            className="w-5 h-5 sm:w-6 sm:h-6 shrink-0"
                          />
                          <i className="fas fa-times-circle text-2xl sm:text-4xl text-red-600" />
                          <span className="text-base sm:text-xl font-bold text-red-800">Falso</span>
                        </label>
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Contador de progreso + Botones */}
              <div className="pt-6 border-t-4 border-gray-300 space-y-3">
                {/* Barra de progreso */}
                {(() => {
                  const totalReales = (selectedPrueba?.preguntas || []).filter(p => !esNoCalificable(p.tipo)).length;
                  const pendientes = getPreguntasPendientes();
                  const contestadas = totalReales - pendientes;
                  const porcentaje = totalReales > 0 ? Math.round((contestadas / totalReales) * 100) : 0;
                  return (
                    <div className="bg-white rounded-xl p-3 border-2 border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-gray-700">
                          Progreso: {contestadas}/{totalReales} preguntas
                        </span>
                        <span className={`text-sm font-bold ${pendientes > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {pendientes > 0 ? `Faltan ${pendientes}` : 'Todas contestadas'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full transition-all duration-500 ${porcentaje === 100 ? 'bg-green-500' : 'bg-orange-400'}`}
                          style={{ width: `${porcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <Button
                    variant="outline"
                    onClick={() => handleCerrarPrueba(false)}
                    className="flex-1 h-12 sm:h-16 text-sm sm:text-lg font-bold border-2 sm:border-4 border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="save-exit-btn"
                  >
                    <i className="fas fa-save mr-2" />
                    Guardar y Continuar Después
                  </Button>
                  <Button
                    onClick={handleEnviarPrueba}
                    className="flex-1 h-12 sm:h-16 text-sm sm:text-lg font-bold bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 shadow-xl border-2 sm:border-4 border-green-300"
                    data-testid="submit-exam-btn"
                  >
                    <i className="fas fa-paper-plane mr-2 text-lg sm:text-2xl" />
                    Enviar Prueba Final
                  </Button>
                </div>
              </div>

              <div className="bg-yellow-50 border-4 border-yellow-300 rounded-2xl p-4 text-center">
                <p className="text-sm text-yellow-800 font-semibold">
                  ⚠️ Una vez enviada, no podrás modificar tus respuestas
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ver Respuestas (Profesor) */}
      <Dialog open={isRespuestasDialogOpen} onOpenChange={setIsRespuestasDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-900">
              📋 Respuestas Recibidas - {selectedPrueba?.titulo}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {respuestasRecibidas.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <i className="fas fa-inbox text-6xl mb-4 text-gray-300" />
                <p className="text-lg">Aún no hay respuestas de estudiantes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {respuestasRecibidas.map(respuesta => (
                  <Card key={respuesta.id} className="border-2 hover:border-purple-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-800">
                            👤 {respuesta.estudiante_nombre}
                          </h3>
                          <div className="flex gap-4 mt-2 text-sm text-gray-600">
                            <span>
                              📅 Enviado: {respuesta.fecha_envio && new Date(respuesta.fecha_envio).getFullYear() > 2000
                                ? new Date(respuesta.fecha_envio).toLocaleString('es-ES')
                                : 'Fecha no disponible'}
                            </span>
                            {respuesta.tiempo_utilizado && (
                              <span>⏱️ Tiempo: {respuesta.tiempo_utilizado} min</span>
                            )}
                            <Badge className={
                              respuesta.estado === 'calificada' ? 'bg-green-500' :
                              respuesta.estado === 'calificando' ? 'bg-yellow-500' : 'bg-gray-500'
                            }>
                              {respuesta.estado === 'calificada' ? '✅ Calificada' :
                               respuesta.estado === 'calificando' ? '⏳ Pendiente' : respuesta.estado}
                            </Badge>
                          </div>
                          {respuesta.estado === 'calificada' && (
                            <div className="mt-2">
                              <span className="text-lg font-bold text-green-600">
                                🎯 Nota: {respuesta.puntos_totales_obtenidos || respuesta.calificacion_final || 0}/{selectedPrueba?.puntos_totales} pts
                              </span>
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleCalificarRespuesta(respuesta)}
                          className={respuesta.estado === 'calificada' 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-purple-600 hover:bg-purple-700'
                          }
                        >
                          <i className={`fas ${respuesta.estado === 'calificada' ? 'fa-eye' : 'fa-edit'} mr-2`} />
                          {respuesta.estado === 'calificada' ? 'Ver Calificación' : 'Calificar'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Sección de estudiantes que NO respondieron */}
            {estudiantesNoRespondieron.length > 0 && (
              <div className="mt-8 border-t-2 border-red-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="fas fa-user-times text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-red-800">
                        Estudiantes que NO Respondieron
                      </h3>
                      <p className="text-sm text-red-600">
                        {estudiantesNoRespondieron.filter(e => !e.ya_calificado_cero).length} sin calificar de {estudiantesNoRespondieron.length} total
                        {infoNoRespondieron?.prueba_vencida && (
                          <span className="ml-2 text-red-700 font-semibold">
                            • Prueba vencida
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {estudiantesNoRespondieron.some(e => !e.ya_calificado_cero) && (
                    <Button
                      onClick={handleCalificarNoRespondieron}
                      disabled={calificandoNoRespondieron}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {calificandoNoRespondieron ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times-circle mr-2" />
                          Calificar con 0 a Todos
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {estudiantesNoRespondieron.map(estudiante => (
                    <div 
                      key={estudiante.id}
                      className={`p-3 rounded-lg border-2 flex items-center justify-between ${
                        estudiante.ya_calificado_cero 
                          ? 'bg-gray-100 border-gray-300' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          estudiante.ya_calificado_cero ? 'bg-gray-300' : 'bg-red-200'
                        }`}>
                          <i className={`fas ${estudiante.ya_calificado_cero ? 'fa-check' : 'fa-user'} text-sm ${
                            estudiante.ya_calificado_cero ? 'text-gray-600' : 'text-red-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{estudiante.nombre}</p>
                          <p className="text-xs text-gray-500">{estudiante.email}</p>
                        </div>
                      </div>
                      {estudiante.ya_calificado_cero ? (
                        <span className="text-sm font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded">
                          ✓ Calificado con 0
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                          Sin responder
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Calificar (Profesor) */}
      <Dialog open={isCalificarDialogOpen} onOpenChange={setIsCalificarDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-900">
              ✍️ Calificar - {respuestaSeleccionada?.estudiante_nombre}
            </DialogTitle>
          </DialogHeader>

          {respuestaSeleccionada && (
            <div className="space-y-6">
              {selectedPrueba?.preguntas.map((pregunta, index) => {
                const respuesta = respuestaSeleccionada.respuestas.find(r => r.pregunta_id === pregunta.id);
                // Saltar tipos que no son respondidos por el alumno
                if (pregunta.tipo === 'texto_informativo' || pregunta.tipo === 'material_apoyo') return null;
                // Si no hay respuesta del alumno pero la pregunta es escala_1_10 (opcional),
                // igual la mostramos vacía para que el profesor vea que estaba ahí.
                if (!respuesta && pregunta.tipo !== 'escala_1_10') return null;

                // Escala 1-10: pregunta no calificable. Mostrar el feedback del
                // alumno como una "card" diferente, sin inputs de calificación.
                if (pregunta.tipo === 'escala_1_10') {
                  const valor = respuesta?.respuesta_seleccionada;
                  return (
                    <Card key={pregunta.id} className="border-2 border-indigo-200 bg-indigo-50">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-indigo-600 text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">
                            <i className="fas fa-chart-bar" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-indigo-900">📊 Valoración del alumno (no calificable)</span>
                              <span className="text-xs text-indigo-700 italic">Sin puntos</span>
                            </div>
                            <p className="text-gray-800 font-medium">{pregunta.enunciado}</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                          {valor ? (
                            <EscalaRating
                              value={valor}
                              etiquetaIzq={pregunta.etiqueta_izquierda}
                              etiquetaDer={pregunta.etiqueta_derecha}
                              readOnly
                            />
                          ) : (
                            <p className="text-sm italic text-gray-500 text-center py-3">
                              El alumno no respondió esta valoración (era opcional).
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <Card key={pregunta.id} className="border-2 border-purple-200">
                    <CardContent className="p-6">
                      {/* Pregunta */}
                      <div className="mb-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-700">
                                {pregunta.tipo === 'desarrollo' ? '📝 Desarrollo' : 
                                 pregunta.tipo === 'multiple' ? '✓ Opción Múltiple' : 
                                 '✓/✗ Verdadero/Falso'}
                              </span>
                              <span className="text-sm font-bold text-purple-600">
                                Máx: {pregunta.puntos} pts
                              </span>
                            </div>
                            <p className="text-gray-800 font-medium">{pregunta.enunciado}</p>
                          </div>
                        </div>
                      </div>

                      {/* Respuesta del estudiante */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-blue-900 mb-2">
                          💬 Respuesta del Estudiante:
                        </h4>
                        {pregunta.tipo === 'desarrollo' && (
                          <p className="text-gray-800 whitespace-pre-wrap">
                            {respuesta.respuesta_texto || '(Sin respuesta)'}
                          </p>
                        )}
                        {(pregunta.tipo === 'multiple' || pregunta.tipo === 'verdadero_falso') && (() => {
                          const sel = respuesta.respuesta_seleccionada;
                          const correcta = pregunta.respuesta_correcta;
                          const opciones = pregunta.opciones || [];
                          // Buscar el texto: en multiple opciones es [{letra, texto}], en V/F la "opción" es la letra misma
                          const textoOpcion = (val) => {
                            if (!val) return '';
                            const opt = opciones.find(o => o.letra === val || o === val);
                            if (opt && typeof opt === 'object' && opt.texto) return opt.texto;
                            if (pregunta.tipo === 'verdadero_falso') {
                              const v = String(val).toLowerCase();
                              if (v === 'v' || v === 'verdadero' || v === 'true') return 'Verdadero';
                              if (v === 'f' || v === 'falso' || v === 'false') return 'Falso';
                            }
                            return '';
                          };
                          const textoSel = textoOpcion(sel);
                          const textoCorrecta = textoOpcion(correcta);
                          const esCorrecta = sel && correcta && String(sel).toLowerCase() === String(correcta).toLowerCase();
                          return (
                            <div className="space-y-2">
                              {!sel && <p className="text-gray-500 italic">(Sin respuesta)</p>}
                              {sel && (
                                <div className={`p-3 rounded-lg border-2 ${esCorrecta ? 'bg-green-100 border-green-400' : 'bg-red-50 border-red-300'}`}>
                                  <div className="flex items-start gap-2">
                                    <span className="font-bold text-lg shrink-0">{sel})</span>
                                    <span className="text-gray-800">{textoSel || <i className="text-gray-400">(opción no encontrada)</i>}</span>
                                  </div>
                                  <div className={`mt-1 text-sm font-bold ${esCorrecta ? 'text-green-700' : 'text-red-700'}`}>
                                    {esCorrecta ? '✓ Correcta' : '✗ Incorrecta'}
                                  </div>
                                </div>
                              )}
                              {!esCorrecta && correcta && (
                                <div className="p-3 rounded-lg border-2 bg-green-100 border-green-400">
                                  <div className="text-xs font-bold text-green-800 mb-1">Respuesta correcta:</div>
                                  <div className="flex items-start gap-2">
                                    <span className="font-bold text-lg shrink-0">{correcta})</span>
                                    <span className="text-gray-800">{textoCorrecta || <i className="text-gray-400">(opción no encontrada)</i>}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Calificación */}
                      <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                        <h4 className="font-bold text-yellow-900 mb-3">
                          ⭐ Tu Calificación:
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="font-semibold">Puntos Obtenidos:</Label>
                            <Input
                              type="number"
                              min="0"
                              max={pregunta.puntos}
                              step="0.5"
                              value={calificaciones[pregunta.id]?.puntos || 0}
                              onChange={(e) => setCalificaciones({
                                ...calificaciones,
                                [pregunta.id]: {
                                  ...calificaciones[pregunta.id],
                                  puntos: e.target.value
                                }
                              })}
                              className="mt-1 text-lg font-bold"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="font-semibold">Comentario:</Label>
                            <Textarea
                              value={calificaciones[pregunta.id]?.comentario || ''}
                              onChange={(e) => setCalificaciones({
                                ...calificaciones,
                                [pregunta.id]: {
                                  ...calificaciones[pregunta.id],
                                  comentario: e.target.value
                                }
                              })}
                              placeholder="Retroalimentación para el estudiante..."
                              rows={3}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Botón guardar */}
              <div className="flex gap-3 pt-4 border-t-2">
                <Button
                  variant="outline"
                  onClick={() => setIsCalificarDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => handleReenviarPrueba(respuestaSeleccionada)}
                  disabled={reenviarLoading}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="reenviar-prueba-btn"
                >
                  {reenviarLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2" />
                      Reenviando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo mr-2" />
                      Reenviar Prueba
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleGuardarCalificacion}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <i className="fas fa-save mr-2" />
                  Guardar Calificación
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ver Resultados (Estudiante) */}
      <Dialog open={isVerResultadosEstudianteDialogOpen} onOpenChange={setIsVerResultadosEstudianteDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-900">
              🎯 Tus Resultados - {selectedPrueba?.titulo}
            </DialogTitle>
          </DialogHeader>

          {miRespuestaCalificada && (
            <div className="space-y-6">
              {/* Resumen */}
              <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-4 border-green-300">
                <CardContent className="p-6">
                  <div className="text-center">
                    <h3 className="text-4xl font-black text-green-700 mb-2">
                      {miRespuestaCalificada.puntos_totales_obtenidos || miRespuestaCalificada.calificacion_final || 0} / {selectedPrueba?.puntos_totales} pts
                    </h3>
                    <p className="text-lg text-gray-600">
                      {Math.round(((miRespuestaCalificada.puntos_totales_obtenidos || 0) / selectedPrueba?.puntos_totales) * 100)}% - 
                      {((miRespuestaCalificada.puntos_totales_obtenidos || 0) / selectedPrueba?.puntos_totales) >= 0.6 ? ' ¡Aprobado! 🎉' : ' No aprobado'}
                    </p>
                    <div className="mt-3 text-sm text-gray-600">
                      📅 Enviado: {miRespuestaCalificada.fecha_envio && new Date(miRespuestaCalificada.fecha_envio).getFullYear() > 2000
                        ? new Date(miRespuestaCalificada.fecha_envio).toLocaleString('es-ES')
                        : 'Fecha no disponible'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preguntas y respuestas */}
              {selectedPrueba?.preguntas.map((pregunta, index) => {
                const miRespuesta = miRespuestaCalificada.respuestas.find(r => r.pregunta_id === pregunta.id);
                if (!miRespuesta) return null;

                return (
                  <Card key={pregunta.id} className="border-2 border-gray-200">
                    <CardContent className="p-6">
                      {/* Pregunta */}
                      <div className="mb-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-indigo-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium mb-2">{pregunta.enunciado}</p>
                            <div className="flex gap-3 text-sm">
                              <span className="text-gray-600">Máx: {pregunta.puntos} pts</span>
                              <span className="font-bold text-green-600">
                                Obtuviste: {miRespuesta.puntos_obtenidos || 0} pts
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Tu respuesta */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-3">
                        <h4 className="font-bold text-blue-900 mb-2">📝 Tu respuesta:</h4>
                        <p className="text-gray-800 whitespace-pre-wrap">
                          {miRespuesta.respuesta_texto || miRespuesta.respuesta_seleccionada || '(Sin respuesta)'}
                        </p>
                      </div>

                      {/* Comentario del profesor */}
                      {miRespuesta.comentario_profesor && (
                        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                          <h4 className="font-bold text-yellow-900 mb-2">💬 Comentario del profesor:</h4>
                          <p className="text-gray-800">{miRespuesta.comentario_profesor}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              <div className="flex justify-center pt-4">
                <Button
                  onClick={() => setIsVerResultadosEstudianteDialogOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Reabrir Prueba */}
      <Dialog open={isReabrirDialogOpen} onOpenChange={setIsReabrirDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto p-0">
          {reabrirPrueba && (
            <>
              {/* Header con gradiente */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 pt-6 pb-5 rounded-t-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <i className="fas fa-lock-open text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg leading-tight">Reabrir Prueba</h2>
                    <p className="text-white/70 text-xs">Dar otra oportunidad a estudiantes</p>
                  </div>
                </div>
                <div className="bg-white/15 backdrop-blur-sm rounded-lg px-4 py-3">
                  <p className="font-semibold text-white text-sm">{reabrirPrueba.titulo}</p>
                  <p className="text-white/80 text-xs mt-0.5">{reabrirPrueba.clase_nombre}</p>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5">
                {/* Nueva fecha límite */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-calendar-alt text-amber-500" />
                    Nueva fecha límite
                  </label>
                  <input
                    type="date"
                    value={reabrirFecha}
                    onChange={(e) => setReabrirFecha(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-4 py-2.5 text-sm transition-colors outline-none"
                    data-testid="reabrir-fecha-input"
                  />
                </div>

                {/* Selección de estudiantes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <i className="fas fa-users text-amber-500" />
                      Estudiantes
                      <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                        {reabrirSeleccionados.length}/{reabrirEstudiantes.length}
                      </span>
                    </label>
                    <button
                      onClick={toggleReabrirTodos}
                      className="text-xs font-medium text-amber-600 hover:text-amber-800 transition-colors"
                      data-testid="reabrir-toggle-all"
                    >
                      {reabrirSeleccionados.length === reabrirEstudiantes.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </button>
                  </div>
                  <div className="border-2 border-gray-200 rounded-xl max-h-52 overflow-y-auto">
                    {reabrirEstudiantes.length === 0 ? (
                      <p className="p-4 text-sm text-gray-400 text-center">No hay estudiantes inscritos</p>
                    ) : (
                      reabrirEstudiantes.map((est, idx) => (
                        <label
                          key={est.id}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                            reabrirSeleccionados.includes(est.id) 
                              ? 'bg-amber-50' 
                              : 'hover:bg-gray-50'
                          } ${idx !== reabrirEstudiantes.length - 1 ? 'border-b border-gray-100' : ''}`}
                          data-testid={`reabrir-est-${est.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={reabrirSeleccionados.includes(est.id)}
                            onChange={() => toggleReabrirEstudiante(est.id)}
                            className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {est.nombre?.[0]}{est.apellido?.[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-700 block truncate">{est.nombre} {est.apellido}</span>
                            {est.ya_calificado_cero && (
                              <span className="text-xs text-red-500">Calificado con 0</span>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl h-11"
                  onClick={() => setIsReabrirDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl h-11 shadow-lg shadow-amber-200 transition-all"
                  onClick={handleConfirmarReabrir}
                  disabled={reabrirLoading || reabrirSeleccionados.length === 0 || !reabrirFecha}
                  data-testid="reabrir-confirmar-btn"
                >
                  {reabrirLoading ? (
                    <><i className="fas fa-spinner fa-spin mr-2" />Reabriendo...</>
                  ) : (
                    <><i className="fas fa-lock-open mr-2" />Reabrir para {reabrirSeleccionados.length}</>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Genesis AI - Generar Preguntas */}
      {selectedPrueba && (
        <GenesisGenerarPreguntas
          open={isGenesisGenOpen}
          onOpenChange={setIsGenesisGenOpen}
          claseId={selectedPrueba.clase_id}
          claseNombre={selectedPrueba.clase_nombre}
          onAccept={handleAceptarPreguntasIA}
        />
      )}

      {/* Modal Resultados Review (histograma de escala_1_10 marcadas como es_review) */}
      <Dialog open={isReviewResultadosOpen} onOpenChange={setIsReviewResultadosOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-review-resultados">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              <i className="fas fa-chart-column text-purple-600" />
              Resultados de Review
            </DialogTitle>
          </DialogHeader>

          {loadingReviewResultados ? (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-spinner fa-spin text-3xl mb-3 text-purple-600" />
              <p>Cargando resultados...</p>
            </div>
          ) : reviewResultados ? (
            <div className="space-y-5">
              {reviewResultados.prueba_titulo && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-3">
                  <p className="text-xs uppercase tracking-wide text-purple-700 font-semibold">Prueba</p>
                  <p className="text-base font-bold text-purple-900">{reviewResultados.prueba_titulo}</p>
                  <p className="text-xs text-purple-700 mt-1">
                    {reviewResultados.total_respuestas_prueba || 0} respuesta(s) recibidas en total
                  </p>
                </div>
              )}

              {(!reviewResultados.preguntas || reviewResultados.preguntas.length === 0) ? (
                <div className="text-center py-10 text-gray-500">
                  <i className="fas fa-inbox text-3xl mb-2 text-gray-400" />
                  <p className="font-semibold">Esta prueba no tiene preguntas marcadas como Review.</p>
                  <p className="text-xs mt-1">Marcá una pregunta tipo &quot;Escala 1-10&quot; como Review desde su edición.</p>
                </div>
              ) : (
                reviewResultados.preguntas.map((preg, idx) => (
                  <div key={preg.pregunta_id || idx} className="border-2 border-purple-200 rounded-xl p-4 bg-white">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">Pregunta {idx + 1}</p>
                        <p className="text-sm sm:text-base font-bold text-gray-800 mt-0.5">{preg.pregunta}</p>
                        {(preg.etiqueta_izquierda || preg.etiqueta_derecha) && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            1 = {preg.etiqueta_izquierda || '—'} · 10 = {preg.etiqueta_derecha || '—'}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500">Promedio</p>
                        <p className="text-2xl font-black text-purple-700">{preg.promedio}</p>
                        <p className="text-[10px] text-gray-500">{preg.total_respondidas} resp.</p>
                      </div>
                    </div>

                    {/* Histograma horizontal 1..10 */}
                    <div className="space-y-1.5" data-testid={`histograma-${preg.pregunta_id || idx}`}>
                      {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((nota) => {
                        const count = preg.histograma?.[String(nota)] || 0;
                        const max = preg.max_count || 1;
                        const pct = max > 0 ? Math.round((count / max) * 100) : 0;
                        const total = preg.total_respondidas || 0;
                        const pctTotal = total > 0 ? Math.round((count / total) * 100) : 0;
                        // Color: rojo (1-3), ámbar (4-6), verde (7-10)
                        const barColor = nota >= 7
                          ? 'bg-emerald-500'
                          : nota >= 4
                            ? 'bg-amber-500'
                            : 'bg-red-500';
                        return (
                          <div key={nota} className="flex items-center gap-2">
                            <div className="w-7 text-right text-sm font-bold text-gray-700 shrink-0">{nota}</div>
                            <div className="flex-1 bg-gray-100 rounded-md h-6 overflow-hidden relative">
                              <div
                                className={`h-full ${barColor} transition-all`}
                                style={{ width: `${pct}%` }}
                              />
                              {count > 0 && (
                                <span className="absolute inset-0 flex items-center px-2 text-xs font-bold text-gray-800">
                                  {count} {pctTotal > 0 && <span className="text-gray-500 ml-1">({pctTotal}%)</span>}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}

              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsReviewResultadosOpen(false)}
                  data-testid="btn-cerrar-review-resultados"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              <p>No hay datos para mostrar.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PruebasExamenes;
